import React, { useState, useEffect, useCallback, useRef } from 'react';
import axios from 'axios';
import './App.css';
// Future Form. Loaded after App.css so the new theme wins wherever the two
// still overlap on a page that has not been converted yet.
import './future-form.css';
import { API_URL } from './utils/api';
import { formatCurrency, formatDateOnly, sanitizeDecimalInput, formatDateTime, parseServerDate } from './utils/format';
import Sidebar from './components/Sidebar';
import ProfileMenu from './components/ProfileMenu';
import LoginPage from './components/LoginPage';
import Dashboard from './components/Dashboard';
import ProjectsList from './components/ProjectsList';
import MayLogicCard from './components/MayLogicCard';
import MayLogicScratchpad from './components/MayLogicScratchpad';
import CaseFinancials from './components/CaseFinancials';
import ProviderReconciliation from './components/ProviderReconciliation';
import CloseoutApprovals from './components/CloseoutApprovals';
import ProviderLookup from './components/ProviderLookup';
import CounterOfferModal from './components/CounterOfferModal';
import LettersTab from './components/LettersTab';
import MadeWholeWaiverModal from './components/MadeWholeWaiverModal';
import MedPayModal from './components/MedPayModal';

// Breadcrumb wording for the header rule. "Cases" is the label the team uses
// for what the tab state still calls projects.
const TAB_TITLES = {
  projects: 'Cases',
  negotiations: 'Negotiations',
  providers: 'Providers',
  closeout: 'Closeout',
  reconciliation: 'Reconciliation',
};


function App() {
  const [projects, setProjects] = useState([]);
  const [refreshingList, setRefreshingList] = useState(false);
  const [selectedProject, setSelectedProject] = useState(null);
  // Persist activeTab across remounts. If any deeply-nested component throws
  // during render (and we don't have an ErrorBoundary inline-able everywhere),
  // React unmounts the whole tree and useState resets to its default —
  // that's the "wiped to dashboard" bug Teresa was seeing. Reading the last
  // tab from sessionStorage on mount means a remount lands the user back where
  // they were instead of on Dashboard.
  const [activeTab, setActiveTab] = useState(() => {
    // A ?closeout=<id> link from the Slack approval message goes straight to
    // the closeout queue, whatever tab was last open.
    try {
      if (new URLSearchParams(window.location.search).get('closeout')) return 'closeout';
    } catch (e) { /* URLSearchParams may be unavailable */ }
    try {
      const saved = sessionStorage.getItem('liens.activeTab');
      if (saved && ['dashboard', 'projects', 'negotiations', 'reconciliation',
                    'providers', 'closeout'].includes(saved)) {
        return saved;
      }
    } catch (e) { /* sessionStorage may be unavailable */ }
    return 'dashboard';
  });
  // Mirror activeTab into sessionStorage on every change so the value above
  // is always fresh on the next mount. Also scroll to top whenever the tab
  // changes so the new view's content lands in the viewport.
  useEffect(() => {
    try { sessionStorage.setItem('liens.activeTab', activeTab); } catch (e) { /* ignore */ }
    window.scrollTo(0, 0);
  }, [activeTab]);
  const [loading, setLoading] = useState(false);
  const [negotiations, setNegotiations] = useState([]);
  const [selectedNegotiation, setSelectedNegotiation] = useState(null);
  // Counter-offer modal. Replaces the old prompt(): the negotiator edits a
  // drafted note and must preview the real email before Send unlocks.
  const [counterModal, setCounterModal] = useState(null);
  // Scroll to top when opening a negotiation detail (declared AFTER
  // selectedNegotiation to avoid a TDZ ReferenceError — the dep array is
  // evaluated at hook-declaration time, before the rest of the component
  // body finishes initializing).
  useEffect(() => {
    if (selectedNegotiation) {
      window.scrollTo(0, 0);
    }
  }, [selectedNegotiation?.id, selectedNegotiation?._id]);
  const [stats, setStats] = useState(null);
  
  // Authentication state
  const [user, setUser] = useState(null);
  const [authLoading, setAuthLoading] = useState(true);
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  
  // Pagination state
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize] = useState(50);
  const [pagination, setPagination] = useState({ total: 0, limit: 50, skip: 0, has_more: false });
  const [searchTerm, setSearchTerm] = useState(''); // Search input (updates immediately)
  // Allowed phases for project filter: set REACT_APP_ALLOWED_PHASES (comma-separated); empty = no restriction, show all phases
  const ALLOWED_PHASES = (() => {
    const raw = process.env.REACT_APP_ALLOWED_PHASES;
    if (raw && typeof raw === 'string') {
      const list = raw.split(',').map((s) => s.trim()).filter(Boolean);
      return list;
    }
    return [];
  })();
  const PHASE_FILTER_ALL = 'All';
  const [phaseFilter, setPhaseFilter] = useState(PHASE_FILTER_ALL); // Default: no filter
  const [apiPhases, setApiPhases] = useState([]); // All phases from API when ALLOWED_PHASES is empty
  
  // Check authentication status
  const checkAuth = useCallback(async () => {
    try {
      const response = await axios.get(`${API_URL}/api/auth/me`, { withCredentials: true });
      setUser(response.data);
      setIsAuthenticated(true);
      return true;
    } catch (error) {
      if (error.response?.status === 401) {
        setIsAuthenticated(false);
        setUser(null);
        // Don't redirect automatically - show login form instead
        return false;
      }
      console.error('Auth check error:', error);
      return false;
    }
  }, []);
  
  // Handle logout: redirect to backend GET /api/auth/logout so cookie clear is same-origin (browser then redirects to frontend)
  const handleLogout = () => {
    setUser(null);
    setIsAuthenticated(false);
    const logoutUrl = API_URL ? `${API_URL}/api/auth/logout` : `${window.location.origin}/api/auth/logout`;
    window.location.href = logoutUrl;
  };

  // Define load functions first with useCallback
  const loadStats = useCallback(async () => {
    setLoading(true);
    try {
      const response = await axios.get(`${API_URL}/api/dashboard/stats`, { withCredentials: true });
      setStats(response.data);
    } catch (error) {
      console.error('Error loading stats:', error);
      setStats(null);
    } finally {
      setLoading(false);
    }
  }, []);

  const loadProjects = useCallback(async (page = 1, search = '', phase = '') => {
    setLoading(true);
    try {
      const skip = (page - 1) * pageSize;
      const params = { limit: pageSize, skip: skip };
      if (search && search.trim()) {
        params.search = search.trim();
      }
      if (phase && phase.trim() && phase !== PHASE_FILTER_ALL) {
        params.phase = phase.trim();
      } else if (ALLOWED_PHASES.length > 0) {
        // When env restricts phases, send allowed list so API returns only those (correct pagination)
        params.allowed_phases = ALLOWED_PHASES.join(",");
      }
      const projectsRes = await axios.get(`${API_URL}/api/projects`, { params, withCredentials: true });
      
      let projectsData = [];
      let paginationData = { total: 0, limit: pageSize, skip: skip, has_more: false };
      
      if (Array.isArray(projectsRes.data)) {
        projectsData = projectsRes.data;
      } else if (projectsRes.data && projectsRes.data.projects) {
        projectsData = projectsRes.data.projects;
        paginationData = projectsRes.data.pagination || paginationData;
      }
      
      // Backend already filters by REACT_APP_ALLOWED_PHASES via env_allowed + the
      // allowed_phases query param. No need to re-filter here — and the previous
      // version overwrote paginationData.total with the local page count when the
      // user selected "All", making "Showing 1 to 50 of 50" appear regardless of
      // the actual total.
      
      setProjects(projectsData);
      setPagination(paginationData);
    } catch (error) {
      console.error('Error loading projects:', error);
      console.error('Error details:', error.response?.data || error.message);
      setProjects([]);
    } finally {
      setLoading(false);
    }
  }, [pageSize]);

  const handleRefreshProjectsList = useCallback(async () => {
    if (refreshingList) return;
    setRefreshingList(true);
    try {
      await axios.post(`${API_URL}/api/refresh`, {}, { withCredentials: true });
      await loadProjects(currentPage, searchTerm, phaseFilter);
    } catch (error) {
      console.error('Error refreshing projects from Filevine:', error);
      alert('Refresh failed. ' + (error.response?.data?.detail || error.message || ''));
    } finally {
      setRefreshingList(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentPage, searchTerm, phaseFilter, refreshingList]);

  useEffect(() => {
    const initAuth = async () => {
      const urlParams = new URLSearchParams(window.location.search);
      const authError = urlParams.get('error');
      const hasCode = urlParams.get('code'); // Google OAuth callback
      
      if (authError === 'auth_failed') {
        alert('Authentication failed. Please try again.');
        window.history.replaceState({}, document.title, window.location.pathname);
      }

      if (hasCode) {
        setAuthLoading(true);
        await new Promise(resolve => setTimeout(resolve, 300));
        const authenticated = await checkAuth();
        setAuthLoading(false);
        if (authenticated && activeTab === 'dashboard') {
          loadStats();
        }
      } else {
        // No code parameter - check auth quickly, but don't show loading screen
        // Just check in background and update state
        checkAuth().then(authenticated => {
          setAuthLoading(false);
          if (authenticated && activeTab === 'dashboard') {
            loadStats();
          }
        }).catch(() => {
          setAuthLoading(false);
        });
      }
    };
    initAuth();
  }, [checkAuth, activeTab, loadStats]);
  
  useEffect(() => {
    if (activeTab === 'dashboard' && isAuthenticated) {
      loadStats();
    }
  }, [activeTab, loadStats, isAuthenticated]);

  // Debounced search - standard pattern: debounce the API call, not the input
  useEffect(() => {
    if (activeTab === 'projects') {
      const timeoutId = setTimeout(() => {
        setCurrentPage(1);
        loadProjects(1, searchTerm, phaseFilter);
      }, 400);
      return () => clearTimeout(timeoutId);
    }
  }, [searchTerm, activeTab, loadProjects, phaseFilter]);

  useEffect(() => {
    if (activeTab === 'projects' && !searchTerm) {
      loadProjects(currentPage, '', phaseFilter);
    }
  }, [activeTab, currentPage, loadProjects, searchTerm, phaseFilter]);

  // When no allowed phases are configured, fetch all phases from API for the dropdown
  useEffect(() => {
    if (activeTab !== 'projects' || ALLOWED_PHASES.length > 0) return;
    let cancelled = false;
    axios.get(`${API_URL}/api/phases`, { withCredentials: true })
      .then((res) => {
        if (!cancelled && res.data && Array.isArray(res.data.phases)) {
          setApiPhases(res.data.phases);
        }
      })
      .catch(() => {});
    return () => { cancelled = true; };
  }, [activeTab]);

  const negotiationsLoadedRef = useRef(false);
  const isLoadingRef = useRef(false);

  // (Manual Accept state moved into NegotiationsList — see comment below.
  // Keeping it at App-component scope caused every keystroke in the notes
  // textarea to re-render App, which recreated the inline-defined
  // NegotiationsList memo component, unmounting the textarea and dropping
  // cursor focus. Local state inside NegotiationsList sidesteps that.)

  const loadNegotiations = useCallback(async () => {
    // Prevent concurrent calls
    if (isLoadingRef.current || negotiationsLoadedRef.current) {
      return;
    }
    isLoadingRef.current = true;
    setLoading(true);
    try {
      const response = await axios.get(`${API_URL}/api/negotiations`, { withCredentials: true });
      setNegotiations(response.data || []);
      negotiationsLoadedRef.current = true;
    } catch (error) {
      console.error('Error loading negotiations:', error);
      setNegotiations([]);
    } finally {
      setLoading(false);
      isLoadingRef.current = false;
    }
  }, []);
  


  const NegotiationsList = React.memo(() => {
    // Filter + search persist across unmount (task #42).
    //
    // WHY sessionStorage instead of lifting to App: opening a negotiation
    // detail swaps the rendered view, unmounting NegotiationsList and
    // dropping its local state — so the search box came back empty and
    // you'd be looking at "all negotiations" again after hitting back.
    // Lifting the state into App would fix persistence but re-introduces
    // the re-render/focus bug described in the comment above (typing in
    // the list re-renders App and unmounts the form). sessionStorage gives
    // us persistence while keeping the state local.
    const [filterStatus, setFilterStatus] = useState(() => {
      try { return sessionStorage.getItem('liens.negFilterStatus') || 'all'; }
      catch (e) { return 'all'; }
    });
    // Client-name search (task #8). Case-insensitive contains-match.
    const [clientSearch, setClientSearch] = useState(() => {
      try { return sessionStorage.getItem('liens.negClientSearch') || ''; }
      catch (e) { return ''; }
    });
    const mountedRef = useRef(false);

    // Mirror both into sessionStorage on every change so a remount restores them.
    useEffect(() => {
      try { sessionStorage.setItem('liens.negFilterStatus', filterStatus); } catch (e) { /* ignore */ }
    }, [filterStatus]);
    useEffect(() => {
      try { sessionStorage.setItem('liens.negClientSearch', clientSearch); } catch (e) { /* ignore */ }
    }, [clientSearch]);

    // Manual Accept (task #27, focus-bug fix in task #29).
    // These hooks MUST live inside NegotiationsList — not in App — so that
    // typing in the notes textarea doesn't re-render App and unmount the
    // form.
    const [manualAcceptOpen, setManualAcceptOpen] = useState(false);
    const [manualAcceptForm, setManualAcceptForm] = useState({
      accepted_amount: '',
      check_payee: '',
      mailing_address: '',
      notes: '',
      send_confirmation_email: true,
    });
    const [manualAcceptSubmitting, setManualAcceptSubmitting] = useState(false);
    const [manualAcceptError, setManualAcceptError] = useState(null);

    useEffect(() => {
      if (!mountedRef.current) {
        mountedRef.current = true;
        loadNegotiations();
      }
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const _negSearch = (clientSearch || '').trim().toLowerCase();
    const filteredNegotiations = negotiations.filter(n => {
      if (filterStatus !== 'all' && n.status !== filterStatus) return false;
      if (_negSearch && !(n.client_name || '').toLowerCase().includes(_negSearch)) return false;
      return true;
    });

    const handleViewNegotiation = async (negotiationId) => {
      try {
        const response = await axios.get(`${API_URL}/api/negotiations/${negotiationId}`, { withCredentials: true });
        setSelectedNegotiation(response.data);
      } catch (error) {
        console.error('Error loading negotiation details:', error);
        alert('Failed to load negotiation details.');
      }
    };

    const handleRespondToCounter = async (negotiationId, action, amount) => {
      if (!negotiationId) {
        alert('Error: Negotiation ID is missing. Please try viewing the negotiation again.');
        console.error('Negotiation ID is undefined. selectedNegotiation:', selectedNegotiation);
        return;
      }
      try {
        await axios.post(
          `${API_URL}/api/negotiations/${negotiationId}/respond`,
          { action, amount: amount ? parseFloat(amount) : null },
          { withCredentials: true }
        );
        alert(`Negotiation ${action}ed successfully!`);
        negotiationsLoadedRef.current = false;
        await loadNegotiations();
        setSelectedNegotiation(null);
      } catch (error) {
        console.error('Error responding to negotiation:', error);
        alert(error.response?.data?.detail || 'Failed to respond to negotiation.');
      }
    };

    if (selectedNegotiation) {
      return (
        <div className="negotiation-detail">
          <button 
            onClick={() => setSelectedNegotiation(null)}
            className="btn-back"
          >
            ← Back to List
          </button>
          <h2>Negotiation Details</h2>
          <table className="negotiation-details-table">
            <tbody>
              <tr>
                <td><strong>CLIENT</strong></td>
                <td>{selectedNegotiation.client_name}</td>
              </tr>
              <tr>
                <td><strong>PROVIDER</strong></td>
                <td>{selectedNegotiation.provider_name}</td>
              </tr>
              <tr>
                <td><strong>STATUS</strong></td>
                <td>
                  <span className={`status-badge status-${selectedNegotiation.status}`}>
                    {selectedNegotiation.status.replace('_', ' ').toUpperCase()}
                  </span>
                </td>
              </tr>
              <tr>
                <td><strong>INITIAL AMOUNT</strong></td>
                <td>{formatCurrency(selectedNegotiation.initial_amount)}</td>
              </tr>
              <tr>
                <td><strong>REQUESTED AMOUNT</strong></td>
                <td>{formatCurrency(selectedNegotiation.requested_amount)}</td>
              </tr>
              {selectedNegotiation.proposed_amount && (
                <tr>
                  <td><strong>COUNTER-OFFER</strong></td>
                  <td>{formatCurrency(selectedNegotiation.proposed_amount)}</td>
                </tr>
              )}
              <tr>
                <td><strong>CREATED</strong></td>
                <td>{formatDateTime(selectedNegotiation.created_at)}</td>
              </tr>
            </tbody>
          </table>
          
          <h3>History</h3>
          {(selectedNegotiation.open_count || 0) > 0 && (
            <div className="history-open-summary">
              <strong>Email opened {selectedNegotiation.open_count}×</strong>
              {selectedNegotiation.first_opened_at && (
                <span> — first {formatDateTime(selectedNegotiation.first_opened_at)}</span>
              )}
              {selectedNegotiation.last_opened_at && selectedNegotiation.open_count > 1 && (
                <span>, last {formatDateTime(selectedNegotiation.last_opened_at)}</span>
              )}
            </div>
          )}
          <div className="history-timeline">
            {selectedNegotiation.history?.map((entry, idx) => {
              // Friendly labels for every history action we record.
              const labels = {
                negotiation_started: 'Negotiation sent',
                countered_by_provider: 'Provider countered',
                counter_accepted_by_law_firm: 'Counter accepted by firm',
                countered_by_law_firm: 'Firm sent new counter',
                rejected_by_provider: 'Provider declined',
                accepted_by_provider: 'Provider accepted',
                email_opened: 'Email opened',
                email_delivered: 'Email delivered',
                email_bounced: 'Email bounced',
                email_complained: 'Marked as spam',
                email_clicked: 'Link clicked',
                email_delivery_delayed: 'Delivery delayed',
                electronic_agreement_accepted: 'E-signature recorded',
                email_corrected: 'Resent to corrected address',
                counter_offer_blocked_terminal_state: 'Counter-offer blocked (already finalized)',
                reminder_sent: 'Reminder sent',
                reminder_failed: 'Reminder FAILED to send',
                medpay_negotiation_opened: 'Med Pay reimbursement opened',
              };
              let label = labels[entry.action] || entry.action.replace(/_/g, ' ');
              // Reminder entries carry which day fired — fold it into the
              // label so the timeline reads "Day 5 reminder sent" (task #45).
              if (entry.action === 'reminder_sent' && entry.day) {
                // `manual: true` means a team member clicked Send now rather
                // than the scheduler firing it — worth distinguishing.
                label = entry.manual
                  ? `Day ${entry.day} reminder sent manually`
                  : `Day ${entry.day} reminder sent`;
              } else if (entry.action === 'reminder_failed' && entry.day) {
                label = `Day ${entry.day} reminder FAILED to send`;
              }
              const isEmailEvent = entry.action && entry.action.startsWith('email_');
              return (
                <div
                  key={idx}
                  className={`history-item${isEmailEvent ? ' history-item-email' : ''}`}
                >
                  <strong>{label}</strong>
                  <div className="history-meta">
                    <span>{formatDateTime(entry.timestamp)}</span>
                    {entry.amount && <span className="history-amount">{formatCurrency(entry.amount)}</span>}
                    {/* Task #42: show which address(es) this went to.
                        `recipients` is on newer sends; older records fall
                        back to the negotiation-level recipient_email so
                        historical rows still show something useful. */}
                    {(() => {
                      const recips = Array.isArray(entry.recipients) && entry.recipients.length
                        ? entry.recipients
                        : (entry.action === 'negotiation_started' && selectedNegotiation.recipient_email
                            ? [selectedNegotiation.recipient_email]
                            : null);
                      if (!recips) return null;
                      return (
                        <span className="history-recipients" title={recips.join(', ')}>
                          to {recips.length === 1 ? recips[0] : `${recips[0]} +${recips.length - 1} more`}
                        </span>
                      );
                    })()}
                    {/* Failed reminders show why, right on the timeline
                        entry — otherwise the failure is silent (task #45). */}
                    {entry.error && (
                      <span style={{ color: '#991b1b', fontSize: '0.78rem', fontStyle: 'italic' }}>
                        {entry.error}
                      </span>
                    )}
                    {entry.user_agent && (
                      <span className="history-ua" title={entry.user_agent}>
                        {/* Show shortest readable form: "Chrome / Mac" */}
                        {(() => {
                          const ua = entry.user_agent;
                          const browser = /Chrome\//.test(ua) ? 'Chrome'
                            : /Firefox\//.test(ua) ? 'Firefox'
                            : /Safari\//.test(ua) && !/Chrome\//.test(ua) ? 'Safari'
                            : /Edg\//.test(ua) ? 'Edge'
                            : 'Browser';
                          const os = /Mac OS|Macintosh/.test(ua) ? 'Mac'
                            : /Windows/.test(ua) ? 'Windows'
                            : /Android/.test(ua) ? 'Android'
                            : /iPhone|iPad|iOS/.test(ua) ? 'iOS'
                            : /Linux/.test(ua) ? 'Linux'
                            : '';
                          return os ? `${browser} on ${os}` : browser;
                        })()}
                      </span>
                    )}
                  </div>
                </div>
              );
            })}
          </div>

          {selectedNegotiation.status === 'counter_offered' && (
            <div className="counter-offer-card">
              <h3>Respond to Counter-Offer</h3>
              <div className="counter-offer-amount">
                Provider Counter-Offer: {formatCurrency(selectedNegotiation.proposed_amount)}
              </div>
              <div>
                <button
                  onClick={() => handleRespondToCounter(selectedNegotiation.id || selectedNegotiation._id, 'accept')}
                  className="btn-primary"
                >
                  Accept Counter-Offer
                </button>
                <button
                  onClick={() => setCounterModal(selectedNegotiation)}
                  className="btn-secondary"
                >
                  Counter Again
                </button>
              </div>
            </div>
          )}

          {/* Counter-offer composer. Drafts a note, shows the figures it is
              based on, and requires a preview of the real email before the
              Send button unlocks — nothing goes to a provider unread. */}
          {counterModal && (
            <CounterOfferModal
              negotiation={counterModal}
              close={() => setCounterModal(null)}
              onSent={async () => {
                setCounterModal(null);
                negotiationsLoadedRef.current = false;
                await loadNegotiations();
                setSelectedNegotiation(null);
              }}
            />
          )}

          {/* Withdraw / Start Over (task #4). Available on the same
              statuses as Manual Accept. Sets status=withdrawn so a new
              negotiation can be started for the same medication. The
              original Accept/Reject token lands on the expired-link page. */}
          {/* Resend to a corrected address (task #43). Lighter-weight than
              withdrawing: rotates the token so links in the WRONG email stop
              working, then re-sends the same offer to the right address. The
              negotiation, its amounts, and its history all survive. */}
          {(selectedNegotiation.status === 'pending'
            || selectedNegotiation.status === 'counter_offered'
            || selectedNegotiation.status === 'pending_acceptance') && (
            <div className="withdraw-card">
              <details>
                <summary className="withdraw-summary">
                  Sent to the wrong email? Resend to a corrected address
                </summary>
                <form
                  className="withdraw-form"
                  onSubmit={async (e) => {
                    e.preventDefault();
                    const newEmail = (e.target.elements.new_email.value || '').trim();
                    const notes = (e.target.elements.resend_notes.value || '').trim();
                    if (!newEmail || !newEmail.includes('@')) {
                      alert('Please enter a valid email address.');
                      return;
                    }
                    if (!window.confirm(
                      `Resend to ${newEmail}?\n\nThe links in the original email will immediately stop working, ` +
                      `so nobody at the old address can accept or counter. The negotiation itself — amounts, ` +
                      `status, and history — stays intact.`
                    )) return;
                    try {
                      const res = await axios.post(
                        `${API_URL}/api/negotiations/${selectedNegotiation.id || selectedNegotiation._id}/resend-to`,
                        { recipient_email: newEmail, notes },
                        { withCredentials: true }
                      );
                      negotiationsLoadedRef.current = false;
                      await loadNegotiations();
                      const fresh = await axios.get(
                        `${API_URL}/api/negotiations/${selectedNegotiation.id || selectedNegotiation._id}`,
                        { withCredentials: true }
                      );
                      setSelectedNegotiation(fresh.data);
                      alert(
                        `Resent to ${res.data.new_recipient}.\n` +
                        `Links sent to ${res.data.previous_recipient || 'the previous address'} are now inactive.`
                      );
                    } catch (err) {
                      alert('Failed to resend: ' + (err.response?.data?.detail || err.message));
                    }
                  }}
                >
                  <p style={{ fontSize: '0.88rem', color: '#4b5563', margin: '0.5rem 0' }}>
                    Currently sent to <strong>{selectedNegotiation.recipient_email || '—'}</strong>.
                    Entering a corrected address will invalidate the old
                    Accept/Reject links and email a fresh set to the new address.
                    No need to withdraw and re-key the negotiation.
                  </p>
                  <input
                    name="new_email"
                    type="email"
                    required
                    placeholder="corrected@provider.com"
                    style={{
                      width: '100%',
                      padding: '0.5rem 0.65rem',
                      border: '1px solid #d1d5db',
                      borderRadius: 4,
                      fontSize: '0.92rem',
                      fontFamily: 'inherit',
                      marginBottom: '0.5rem',
                    }}
                  />
                  <input
                    name="resend_notes"
                    type="text"
                    placeholder='Optional note — e.g. "typo in domain"'
                    style={{
                      width: '100%',
                      padding: '0.5rem 0.65rem',
                      border: '1px solid #d1d5db',
                      borderRadius: 4,
                      fontSize: '0.92rem',
                      fontFamily: 'inherit',
                    }}
                  />
                  <button type="submit" className="btn-secondary" style={{ marginTop: '0.6rem' }}>
                    Resend to this address
                  </button>
                </form>
              </details>
            </div>
          )}

          {(selectedNegotiation.status === 'pending'
            || selectedNegotiation.status === 'counter_offered'
            || selectedNegotiation.status === 'pending_acceptance') && (
            <div className="withdraw-card">
              <details>
                <summary className="withdraw-summary">
                  Made a mistake? Withdraw and start over
                </summary>
                <form
                  className="withdraw-form"
                  onSubmit={async (e) => {
                    e.preventDefault();
                    const notes = (e.target.elements.notes.value || '').trim();
                    if (!notes) {
                      alert('Please enter a short reason for the withdrawal.');
                      return;
                    }
                    if (!window.confirm(
                      'Withdraw this negotiation? The original Accept/Reject link will show "expired" if the provider clicks it. You can then start a new negotiation for the same medication.'
                    )) return;
                    try {
                      await axios.post(
                        `${API_URL}/api/negotiations/${selectedNegotiation.id || selectedNegotiation._id}/withdraw`,
                        { notes },
                        { withCredentials: true }
                      );
                      negotiationsLoadedRef.current = false;
                      await loadNegotiations();
                      const fresh = await axios.get(
                        `${API_URL}/api/negotiations/${selectedNegotiation.id || selectedNegotiation._id}`,
                        { withCredentials: true }
                      );
                      setSelectedNegotiation(fresh.data);
                    } catch (err) {
                      alert('Failed to withdraw: ' + (err.response?.data?.detail || err.message));
                    }
                  }}
                >
                  <p style={{ fontSize: '0.88rem', color: '#4b5563', margin: '0.5rem 0' }}>
                    Use this if the negotiation went out with a mistake — wrong
                    amount, wrong recipient, etc. The team can then start a
                    fresh negotiation from the Meds tab.
                  </p>
                  <textarea
                    name="notes"
                    required
                    rows={2}
                    placeholder='e.g. "Wrong amount entered — meant $18,000 not $1,800."'
                    style={{
                      width: '100%',
                      padding: '0.5rem 0.65rem',
                      border: '1px solid #d1d5db',
                      borderRadius: 4,
                      fontSize: '0.92rem',
                      fontFamily: 'inherit',
                      resize: 'vertical',
                    }}
                  />
                  <button
                    type="submit"
                    className="btn-danger-outline"
                    style={{ marginTop: '0.5rem' }}
                  >
                    Withdraw Negotiation
                  </button>
                </form>
              </details>
            </div>
          )}

          {/* Withdrawn banner — shown on records that have been withdrawn so
              the audit trail is visible. */}
          {selectedNegotiation.status === 'withdrawn' && (
            <div className="withdrawn-banner">
              <strong>Withdrawn</strong>
              {selectedNegotiation.withdrawn_by_name && (
                <> by {selectedNegotiation.withdrawn_by_name}</>
              )}
              {selectedNegotiation.withdrawn_at && (
                <> on {formatDateTime(selectedNegotiation.withdrawn_at)}</>
              )}
              {selectedNegotiation.withdrawn_notes && (
                <div style={{ marginTop: '0.4rem', fontSize: '0.88rem', color: '#4b5563' }}>
                  <em>Reason:</em> {selectedNegotiation.withdrawn_notes}
                </div>
              )}
            </div>
          )}

          {/* Void Acceptance — accepted negotiations only. Use when a provider
              clicked Accept but then countered via email, or the team entered a
              manual acceptance in error. Resets status to 'pending' so the team
              can update the amount and re-send (or use Manual Accept with the
              new agreed terms). All prior acceptance data is preserved in
              voided_acceptances for the audit trail. */}
          {selectedNegotiation.status === 'accepted' && (
            <div className="withdraw-card">
              <details>
                <summary className="withdraw-summary">
                  Need to undo this acceptance? Void and reopen
                </summary>
                <form
                  className="withdraw-form"
                  onSubmit={async (e) => {
                    e.preventDefault();
                    const notes = (e.target.elements.notes.value || '').trim();
                    if (!notes) {
                      alert('Please enter a short reason for voiding the acceptance.');
                      return;
                    }
                    if (!window.confirm(
                      'Void this acceptance? Status will reset to pending and the captured payment details will be cleared. The original acceptance will remain in the audit history. This does NOT email the provider.'
                    )) return;
                    try {
                      await axios.post(
                        `${API_URL}/api/negotiations/${selectedNegotiation.id || selectedNegotiation._id}/void-acceptance`,
                        { notes },
                        { withCredentials: true }
                      );
                      negotiationsLoadedRef.current = false;
                      await loadNegotiations();
                      const fresh = await axios.get(
                        `${API_URL}/api/negotiations/${selectedNegotiation.id || selectedNegotiation._id}`,
                        { withCredentials: true }
                      );
                      setSelectedNegotiation(fresh.data);
                    } catch (err) {
                      alert('Failed to void acceptance: ' + (err.response?.data?.detail || err.message));
                    }
                  }}
                >
                  <p style={{ fontSize: '0.88rem', color: '#4b5563', margin: '0.5rem 0' }}>
                    Use this when a provider clicked Accept but then countered
                    via email, or if the team entered a manual acceptance in
                    error. Status flips back to pending so you can update the
                    amount and re-send the negotiation.
                  </p>
                  <textarea
                    name="notes"
                    required
                    rows={2}
                    placeholder='e.g. "Provider countered via email at $7,500 after clicking Accept."'
                    style={{
                      width: '100%',
                      padding: '0.5rem 0.65rem',
                      border: '1px solid #d1d5db',
                      borderRadius: 4,
                      fontSize: '0.92rem',
                      fontFamily: 'inherit',
                      resize: 'vertical',
                    }}
                  />
                  <button
                    type="submit"
                    className="btn-danger-outline"
                    style={{ marginTop: '0.5rem' }}
                  >
                    Void Acceptance
                  </button>
                </form>
              </details>
            </div>
          )}

          {/* Voided-acceptance audit banner — shown on negotiations that were
              previously accepted then voided. Surfaces the history so the team
              can see what happened. The most recent void is shown most prominently. */}
          {Array.isArray(selectedNegotiation.voided_acceptances)
            && selectedNegotiation.voided_acceptances.length > 0 && (
            <div className="withdrawn-banner" style={{ background: '#fef3c7', borderColor: '#f59e0b' }}>
              <strong>
                {selectedNegotiation.voided_acceptances.length === 1
                  ? 'Acceptance voided'
                  : `Acceptance voided ${selectedNegotiation.voided_acceptances.length} times`}
              </strong>
              {(() => {
                const latest = selectedNegotiation.voided_acceptances[
                  selectedNegotiation.voided_acceptances.length - 1
                ];
                return (
                  <>
                    {latest.voided_by_name && <> by {latest.voided_by_name}</>}
                    {latest.voided_at && (
                      <> on {formatDateTime(latest.voided_at)}</>
                    )}
                    {latest.notes && (
                      <div style={{ marginTop: '0.4rem', fontSize: '0.88rem', color: '#4b5563' }}>
                        <em>Reason:</em> {latest.notes}
                      </div>
                    )}
                  </>
                );
              })()}
            </div>
          )}

          {/* Manual Accept — task #27. Available on pending / counter_offered
              negotiations for cases where the provider replied via email or
              phone instead of clicking the Accept link. */}
          {(selectedNegotiation.status === 'pending'
            || selectedNegotiation.status === 'counter_offered'
            || selectedNegotiation.status === 'pending_acceptance') && (
            <div className="manual-accept-card">
              {!manualAcceptOpen ? (
                <>
                  <h3>Manual Accept</h3>
                  <p style={{ color: 'var(--text-secondary)', fontSize: '0.9rem', margin: '0.25rem 0 0.75rem 0' }}>
                    Use this if the provider replied to the email or phone instead of clicking Accept.
                  </p>
                  <button type="button" className="btn-secondary" onClick={() => {
                    const defaultAmount = selectedNegotiation.proposed_amount
                      || selectedNegotiation.current_amount
                      || selectedNegotiation.requested_amount
                      || '';
                    setManualAcceptForm({
                      accepted_amount: String(defaultAmount || ''),
                      check_payee: selectedNegotiation.provider_name || '',
                      mailing_address: '',
                      notes: '',
                      send_confirmation_email: true,
                    });
                    setManualAcceptOpen(true);
                  }}>
                    Mark as Accepted
                  </button>
                </>
              ) : (
                <form onSubmit={async (e) => {
                  e.preventDefault();
                  if (manualAcceptSubmitting) return;
                  setManualAcceptError(null);
                  // Client-side validation
                  const amount = parseFloat(manualAcceptForm.accepted_amount);
                  if (!(amount > 0)) {
                    setManualAcceptError('Accepted amount must be greater than 0.');
                    return;
                  }
                  if (!manualAcceptForm.check_payee.trim()) {
                    setManualAcceptError('Check payee is required.');
                    return;
                  }
                  if (!manualAcceptForm.mailing_address.trim()) {
                    setManualAcceptError('Mailing address is required.');
                    return;
                  }
                  if (!manualAcceptForm.notes.trim()) {
                    setManualAcceptError('Notes are required — please record why this was manually accepted.');
                    return;
                  }
                  setManualAcceptSubmitting(true);
                  try {
                    await axios.post(
                      `${API_URL}/api/negotiations/${selectedNegotiation.id || selectedNegotiation._id}/manual-accept`,
                      {
                        accepted_amount: amount,
                        check_payee: manualAcceptForm.check_payee.trim(),
                        mailing_address: manualAcceptForm.mailing_address.trim(),
                        notes: manualAcceptForm.notes.trim(),
                        send_confirmation_email: !!manualAcceptForm.send_confirmation_email,
                      },
                      { withCredentials: true }
                    );
                    setManualAcceptOpen(false);
                    setManualAcceptForm({ accepted_amount: '', check_payee: '', mailing_address: '', notes: '', send_confirmation_email: true });
                    // Force loadNegotiations to actually re-fetch (the ref-guard
                    // would otherwise return cached results)
                    negotiationsLoadedRef.current = false;
                    await loadNegotiations();
                    const fresh = await axios.get(
                      `${API_URL}/api/negotiations/${selectedNegotiation.id || selectedNegotiation._id}`,
                      { withCredentials: true }
                    );
                    setSelectedNegotiation(fresh.data);
                  } catch (err) {
                    setManualAcceptError(err.response?.data?.detail || err.message || 'Failed to manually accept');
                  } finally {
                    setManualAcceptSubmitting(false);
                  }
                }}>
                  <h3>Manual Accept</h3>
                  <p style={{ color: 'var(--text-secondary)', fontSize: '0.85rem', margin: '0.25rem 0 0.75rem 0' }}>
                    The history timeline will record who marked this and why.
                  </p>
                  <div className="manual-accept-grid">
                    <label>
                      Accepted Amount ($)
                      <input
                        type="number" step="0.01" required
                        value={manualAcceptForm.accepted_amount}
                        onChange={(e) => setManualAcceptForm({ ...manualAcceptForm, accepted_amount: e.target.value })}
                      />
                    </label>
                    <label>
                      Check Payee Name
                      <input
                        type="text" required
                        value={manualAcceptForm.check_payee}
                        onChange={(e) => setManualAcceptForm({ ...manualAcceptForm, check_payee: e.target.value })}
                      />
                    </label>
                  </div>
                  <label className="manual-accept-fullwidth">
                    Mailing Address
                    <textarea
                      required rows={3}
                      placeholder="Street, City, State, ZIP"
                      value={manualAcceptForm.mailing_address}
                      onChange={(e) => setManualAcceptForm({ ...manualAcceptForm, mailing_address: e.target.value })}
                    />
                  </label>
                  <label className="manual-accept-fullwidth">
                    Notes (required)
                    <textarea
                      required rows={2}
                      placeholder='e.g. "Provider replied by email on 5/26 confirming $1,800. Email thread saved in Filevine."'
                      value={manualAcceptForm.notes}
                      onChange={(e) => setManualAcceptForm({ ...manualAcceptForm, notes: e.target.value })}
                    />
                  </label>
                  <label className="manual-accept-checkbox">
                    <input
                      type="checkbox"
                      checked={manualAcceptForm.send_confirmation_email}
                      onChange={(e) => setManualAcceptForm({ ...manualAcceptForm, send_confirmation_email: e.target.checked })}
                    />
                    Send confirmation email to provider (creates Resend paper trail with open tracking)
                  </label>
                  {manualAcceptError && (
                    <div style={{ color: '#dc2626', marginTop: '0.5rem', fontSize: '0.88rem' }}>{manualAcceptError}</div>
                  )}
                  <div style={{ display: 'flex', gap: '0.5rem', marginTop: '0.75rem' }}>
                    <button type="button" className="btn-secondary" onClick={() => { setManualAcceptOpen(false); setManualAcceptError(null); }}>
                      Cancel
                    </button>
                    <button type="submit" className="btn-primary" disabled={manualAcceptSubmitting}>
                      {manualAcceptSubmitting ? 'Saving…' : 'Confirm Manual Accept'}
                    </button>
                  </div>
                </form>
              )}
            </div>
          )}

          {/* Reminder Schedule card — task #34. Shown for negotiations
              still in a state where providers might receive automated
              reminders. Lets the team toggle reminders off per negotiation
              and see when each Day 2/5/7 nudge has fired or is due. */}
          {['pending', 'pending_acceptance'].includes(selectedNegotiation.status) && (
            <div className="reminder-schedule-card">
              <div className="reminder-schedule-header">
                <h3>Reminder Schedule</h3>
                {selectedNegotiation.reminders_dry_run && (
                  <span className="reminder-dry-run-tag" title="Automated scheduler is in dry-run mode: it logs what it would send without emailing providers. The 'Send now' button still sends real emails. Flip REMINDERS_DRY_RUN=false in Beanstalk env when ready to take the scheduler live.">
                    Scheduler: dry run
                  </span>
                )}
              </div>
              <label className="reminder-toggle">
                <input
                  type="checkbox"
                  defaultChecked={selectedNegotiation.reminders_enabled !== false}
                  onChange={async (e) => {
                    const enabled = e.target.checked;
                    try {
                      await axios.patch(
                        `${API_URL}/api/negotiations/${selectedNegotiation.id || selectedNegotiation._id}/reminders`,
                        { enabled },
                        { withCredentials: true }
                      );
                      const fresh = await axios.get(
                        `${API_URL}/api/negotiations/${selectedNegotiation.id || selectedNegotiation._id}`,
                        { withCredentials: true }
                      );
                      setSelectedNegotiation(fresh.data);
                    } catch (err) {
                      alert('Failed to update reminder setting: ' + (err.response?.data?.detail || err.message));
                    }
                  }}
                />
                <span>Send automated reminders to provider</span>
              </label>
              <ul className="reminder-rows">
                {(selectedNegotiation.reminder_schedule || []).map((row) => {
                  const fmtPacific = (iso) => {
                    // parseServerDate, not new Date(): these timestamps carry
                    // no zone marker and would otherwise be read as local.
                    const d = parseServerDate(iso);
                    if (!d) return '';
                    return d.toLocaleString('en-US', {
                      timeZone: 'America/Los_Angeles',
                      month: 'short', day: 'numeric',
                      hour: 'numeric', minute: '2-digit', hour12: true,
                    }) + ' PT';
                  };
                  const isSent = row.status === 'sent';
                  return (
                    <li key={row.day} className={`reminder-row ${isSent ? 'is-sent' : 'is-scheduled'}`}>
                      <span className={`reminder-dot day-${row.day}`} />
                      <span className="reminder-day-label">Day {row.day}</span>
                      <span className="reminder-detail">
                        {isSent ? (
                          <>
                            Sent {fmtPacific(row.fired_at)}
                            {row.error && <em style={{ marginLeft: 6, color: '#991b1b' }}>(error: {row.error})</em>}
                          </>
                        ) : (
                          <>
                            Due {fmtPacific(row.due_at)}
                            {/* The "(scheduler dry-ran …)" note used to render
                                here. Hidden as of task #45 — it only ever
                                appeared on negotiations from the dry-run
                                period, and once reminders went live it read
                                as noise rather than useful history. The
                                dry_run_logged_at value is still stored on the
                                row, so this is a display change only and can
                                be reinstated by restoring the block below.

                                {row.dry_run_logged_at && (
                                  <em style={{ marginLeft: 6, color: '#854F0B', fontSize: '0.82rem' }}>
                                    (scheduler dry-ran {fmtPacific(row.dry_run_logged_at)})
                                  </em>
                                )}
                            */}
                          </>
                        )}
                      </span>
                      {!isSent && selectedNegotiation.reminders_enabled !== false && (
                        <button
                          type="button"
                          className="reminder-send-now"
                          title="Send this reminder now, ignoring schedule. This always sends a real email even when scheduler dry-run is on."
                          onClick={async () => {
                            const recipient = selectedNegotiation.recipient_email || 'the provider';
                            if (!window.confirm(`Send a Day ${row.day} reminder email to ${recipient} right now?`)) return;
                            try {
                              const resp = await axios.post(
                                `${API_URL}/api/negotiations/${selectedNegotiation.id || selectedNegotiation._id}/reminders/send-now`,
                                { day: row.day },
                                { withCredentials: true }
                              );
                              const fresh = await axios.get(
                                `${API_URL}/api/negotiations/${selectedNegotiation.id || selectedNegotiation._id}`,
                                { withCredentials: true }
                              );
                              setSelectedNegotiation(fresh.data);
                              // Confirm what actually happened so the user
                              // isn't left guessing whether the email left.
                              if (resp.data?.resend_id) {
                                alert(`Day ${row.day} reminder sent to ${recipient}. Resend ID: ${resp.data.resend_id}`);
                              } else if (resp.data?.dry_run) {
                                alert(`Logged as dry run only — no email sent. (This shouldn't happen on Send Now anymore; please report.)`);
                              } else {
                                alert(`Day ${row.day} reminder sent.`);
                              }
                            } catch (err) {
                              alert('Failed to send: ' + (err.response?.data?.detail || err.message));
                            }
                          }}
                        >
                          Send now
                        </button>
                      )}
                    </li>
                  );
                })}
              </ul>
            </div>
          )}

          {/* Surface the manual-accept context once the negotiation is accepted so
              anyone reviewing later knows it wasn't a provider-clicked accept. */}
          {selectedNegotiation.status === 'accepted' && selectedNegotiation.manual_accept?.was_manual && (
            <div className="manual-accept-banner">
              <strong>Manually accepted</strong> by {selectedNegotiation.manual_accept.accepted_by_name || selectedNegotiation.manual_accept.accepted_by_email}
              {' '}on {formatDateTime(selectedNegotiation.manual_accept.accepted_at)}
              {selectedNegotiation.manual_accept.notes && (
                <div style={{ marginTop: '0.4rem', fontSize: '0.88rem', color: '#4b5563' }}>
                  <em>Note:</em> {selectedNegotiation.manual_accept.notes}
                </div>
              )}
            </div>
          )}

          {/* Med Pay carriers never submit payment details — nothing is emailed
              to them — so their address comes off the negotiation itself. Without
              that in the condition, the card and the letter button below it never
              rendered for Med Pay. */}
          {selectedNegotiation.status === 'accepted' && (selectedNegotiation.payment_mailing_address || selectedNegotiation.payment_check_payee || selectedNegotiation.recipient_address_lines?.length > 0) && (
            <div className="payment-details-card">
              <h3>Payment Details</h3>
              <table className="payment-details-table">
                <tbody>
                  {!selectedNegotiation.payment_mailing_address && selectedNegotiation.recipient_address_lines?.length > 0 && (
                    <tr>
                      <td><strong>MAILING ADDRESS</strong></td>
                      <td>
                        {[selectedNegotiation.recipient_dept, ...selectedNegotiation.recipient_address_lines]
                          .filter(Boolean)
                          .map((line, i) => <div key={i}>{line}</div>)}
                      </td>
                    </tr>
                  )}
                  {selectedNegotiation.payment_mailing_address && (
                    <tr>
                      <td><strong>MAILING ADDRESS</strong></td>
                      <td>{selectedNegotiation.payment_mailing_address}</td>
                    </tr>
                  )}
                  {selectedNegotiation.payment_check_payee && (
                    <tr>
                      <td><strong>CHECK PAYEE NAME</strong></td>
                      <td>{selectedNegotiation.payment_check_payee}</td>
                    </tr>
                  )}
                  {selectedNegotiation.payment_details_submitted_at && (
                    <tr>
                      <td><strong>SUBMITTED</strong></td>
                      <td>{formatDateTime(selectedNegotiation.payment_details_submitted_at)}</td>
                    </tr>
                  )}
                </tbody>
              </table>
              {/* Lien Payment Letter generator — task #23. Available only on accepted negotiations
                  that have payment details captured, so Finance can print + mail the check. */}
              <div style={{ marginTop: '1rem' }}>
                <button
                  type="button"
                  className="btn-primary"
                  onClick={async () => {
                    try {
                      const resp = await axios.post(
                        `${API_URL}/api/negotiations/${selectedNegotiation.id || selectedNegotiation._id}/lien-payment-letter`,
                        {},
                        { withCredentials: true, responseType: 'blob' }
                      );
                      const blob = new Blob([resp.data], {
                        type: 'application/vnd.openxmlformats-officedocument.wordprocessingml.document'
                      });
                      // Try to pull filename from Content-Disposition; otherwise use a sensible default.
                      const cd = resp.headers['content-disposition'] || '';
                      const m = cd.match(/filename="?([^"]+)"?/);
                      const filename = m ? m[1] : 'Lien Payment Letter.docx';
                      const url = window.URL.createObjectURL(blob);
                      const a = document.createElement('a');
                      a.href = url;
                      a.download = filename;
                      document.body.appendChild(a);
                      a.click();
                      a.remove();
                      window.URL.revokeObjectURL(url);
                    } catch (e) {
                      alert('Failed to generate payment letter: ' + (e.response?.data?.detail || e.message));
                    }
                  }}
                >
                  Generate Lien Payment Letter (.docx)
                </button>
              </div>
            </div>
          )}
        </div>
      );
    }

    const STATUS_TONE = {
      accepted: '',
      counter_offered: ' lm-warn',
      rejected: ' lm-neutral',
      withdrawn: ' lm-neutral',
      pending: ' lm-neutral',
      pending_acceptance: ' lm-neutral',
      sent: ' lm-neutral',
    };

    // The same six options the select carried. Rendered as chips rather than
    // a dropdown — identical behaviour, and the whole set is readable at a
    // glance instead of hidden behind a click.
    const FILTERS = [
      ['all', 'All'],
      ['pending', 'Pending'],
      ['counter_offered', 'Counter-offered'],
      ['accepted', 'Accepted'],
      ['rejected', 'Rejected'],
      ['withdrawn', 'Withdrawn'],
    ];

    return (
      <>
        <div className="lm-eyebrow">Keep every conversation moving</div>
        <h1>Negotiations</h1>
        <p className="lm-subtitle">The offer, the response, and exactly what needs to happen next.</p>

        <div className="lm-toolbar">
          <label className="lm-search">
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.7"
                 strokeLinecap="round" aria-hidden="true">
              <circle cx="11" cy="11" r="7" /><path d="m20 20-3.2-3.2" />
            </svg>
            <input type="text" value={clientSearch}
                   onChange={(e) => setClientSearch(e.target.value)}
                   placeholder="Search by client name…"
                   aria-label="Search negotiations by client name" />
          </label>
        </div>

        <div className="lm-tabs" role="tablist" aria-label="Filter by status">
          {FILTERS.map(([value, label]) => (
            <button key={value} type="button" role="tab"
                    aria-selected={filterStatus === value}
                    onClick={() => setFilterStatus(value)}>
              {label}
            </button>
          ))}
        </div>

        <section className="lm-panel">
          <div className="lm-rowhead">
            <h2>Your work queue</h2>
            {(clientSearch || filterStatus !== 'all') && (
              <span className="lm-inline-muted">
                Showing {filteredNegotiations.length} of {negotiations.length}
              </span>
            )}
          </div>

          {loading ? (
            <div className="lm-loading">Loading negotiations…</div>
          ) : filteredNegotiations.length === 0 ? (
            <div className="lm-empty">
              {clientSearch || filterStatus !== 'all'
                ? 'No negotiations match that filter.'
                : 'No negotiations yet.'}
            </div>
          ) : (
            <div className="lm-tablewrap">
              <table>
                <thead>
                  <tr>
                    <th>Provider and client</th>
                    {/* Task #42: Original Bill / Initial Offer / Counter.
                        Replaces the old Initial Amount / Current Amount pair,
                        which didn't show what the provider countered with. */}
                    <th className="lm-num">Original bill</th>
                    <th className="lm-num">Your offer</th>
                    <th className="lm-num">Their response</th>
                    <th>Status</th>
                    <th>Created</th>
                    <th aria-label="View" />
                  </tr>
                </thead>
                <tbody>
                  {filteredNegotiations.map((neg) => {
                    // Compute stale / needs-follow-up indicators from the
                    // reminder anchor (task #34). Only applies while the
                    // provider hasn't responded.
                    const eligible = ['pending', 'pending_acceptance'].includes(neg.status)
                      && neg.reminders_enabled !== false;
                    const anchor = neg.last_law_firm_email_at
                      ? new Date(neg.last_law_firm_email_at)
                      : (neg.created_at ? new Date(neg.created_at) : null);
                    const daysSince = (anchor && eligible)
                      ? (Date.now() - anchor.getTime()) / 86400000
                      : 0;
                    const isStale = eligible && daysSince >= 5 && daysSince < 7;
                    const needsFollowUp = eligible && daysSince >= 7;
                    const tone = STATUS_TONE[neg.status] !== undefined
                      ? STATUS_TONE[neg.status] : ' lm-neutral';
                    return (
                      <tr key={neg.id}>
                        <td>
                          <strong>{neg.provider_name}</strong>
                          <small>{neg.client_name}</small>
                        </td>
                        {/* Original Bill — this provider's billed amount. Falls
                            back to initial_amount for negotiations created
                            before original_bill was captured (task #42). */}
                        <td className="lm-num">
                          {neg.original_bill != null
                            ? formatCurrency(neg.original_bill)
                            : (neg.initial_amount != null ? formatCurrency(neg.initial_amount) : '—')}
                        </td>
                        {/* What the firm first asked them to accept. */}
                        <td className="lm-num">
                          {neg.requested_amount != null ? formatCurrency(neg.requested_amount) : '—'}
                        </td>
                        {/* What the provider came back with, if anything. */}
                        <td className="lm-num">
                          {neg.proposed_amount != null ? formatCurrency(neg.proposed_amount) : '—'}
                        </td>
                        <td>
                          <span className={`lm-tag${tone}`}>
                            {neg.status.replace(/_/g, ' ').replace(/^./, (c) => c.toUpperCase())}
                          </span>
                          {isStale && (
                            <span className="lm-tag lm-alert" title={`${daysSince.toFixed(0)} days since last firm email`}>
                              stale
                            </span>
                          )}
                          {needsFollowUp && (
                            <span className="lm-tag lm-alert" title={`${daysSince.toFixed(0)} days since last firm email — no provider response`}>
                              needs follow-up
                            </span>
                          )}
                        </td>
                        <td>{new Date(neg.created_at).toLocaleDateString()}</td>
                        <td className="lm-num">
                          <button type="button" className="lm-link"
                                  onClick={() => handleViewNegotiation(neg.id)}>
                            View →
                          </button>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </section>
      </>
    );
  });


  // -------------------------------------------------------------------------
  // Common Fund modal (task #22) — opened from the Liens tab. User enters
  // settlement / fees / costs (defaults pre-filled from project_overlay), the
  // modal live-calculates the reduced lien using the Common Fund Doctrine
  // formula, and "Send" posts to /api/projects/{id}/liens/{lien_id}/common-fund/send.
  const CommonFundModal = ({ modal, close, sending, setSending, onSent }) => {
    const lien = modal.lien;
    const projectId = modal.projectId;
    const [settlement, setSettlement] = useState('');
    const [fees, setFees] = useState('');
    const [costs, setCosts] = useState('');
    const [originalLien, setOriginalLien] = useState(lien.original_lien_amount || lien.current_lien_amount || '');
    const [recipientEmail, setRecipientEmail] = useState(lien.lienholder_email || lien.adjuster_email || '');
    const [error, setError] = useState(null);

    // Pre-fill from per-case defaults so the user types these numbers once per case
    useEffect(() => {
      let cancelled = false;
      (async () => {
        try {
          const r = await axios.get(
            `${API_URL}/api/projects/${projectId}/common-fund-defaults`,
            { withCredentials: true }
          );
          if (cancelled) return;
          const d = r.data || {};
          // Prefer per-lien saved inputs over case defaults
          const saved = lien.common_fund_inputs || {};
          if (saved.settlement_amount) setSettlement(String(saved.settlement_amount));
          else if (d.settlement_amount) setSettlement(String(d.settlement_amount));
          if (saved.attorney_fees != null) setFees(String(saved.attorney_fees));
          else if (d.attorney_fees != null) setFees(String(d.attorney_fees));
          if (saved.case_costs != null) setCosts(String(saved.case_costs));
          else if (d.case_costs != null) setCosts(String(d.case_costs));
          if (saved.original_lien_amount) setOriginalLien(String(saved.original_lien_amount));
        } catch (e) { /* defaults are optional; ignore */ }
      })();
      return () => { cancelled = true; };
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [projectId, lien.id]);

    // Live calculation
    const numSettlement = parseFloat(settlement) || 0;
    const numFees = parseFloat(fees) || 0;
    const numCosts = parseFloat(costs) || 0;
    const numOriginal = parseFloat(originalLien) || 0;
    const totalFeesCosts = numFees + numCosts;
    const pct = numSettlement > 0 ? (totalFeesCosts / numSettlement) : 0;
    const reduced = Math.max(0, numOriginal - (numOriginal * pct));

    const canSend = (
      numSettlement > 0 &&
      numOriginal > 0 &&
      recipientEmail && recipientEmail.includes('@') &&
      !sending
    );

    const send = async () => {
      if (!canSend) return;
      setError(null);
      setSending(true);
      try {
        await axios.post(
          `${API_URL}/api/projects/${projectId}/liens/${lien.id}/common-fund/send`,
          {
            recipient_email: recipientEmail.trim(),
            original_lien_amount: numOriginal,
            settlement_amount: numSettlement,
            attorney_fees: numFees,
            case_costs: numCosts,
            reduced_lien_amount: reduced,
          },
          { withCredentials: true }
        );
        onSent();
      } catch (e) {
        setError(e.response?.data?.detail || e.message || 'Failed to send Common Fund email');
        setSending(false);
      }
    };

    return (
      <div className="cf-modal-backdrop" onClick={close}>
        <div className="cf-modal" onClick={(e) => e.stopPropagation()}>
          <div className="cf-modal-header">
            <h3 className="cf-modal-title">Common Fund Reduction — {lien.lienholder_name}</h3>
            <button type="button" className="cf-modal-close" onClick={close} aria-label="Close">&times;</button>
          </div>
          <div className="cf-modal-body">
            <div className="cf-modal-section">
              <div className="cf-modal-section-title">Lien</div>
              <div className="cf-modal-row"><span>Lienholder</span><strong>{lien.lienholder_name}</strong></div>
              {lien.file_number && <div className="cf-modal-row"><span>File #</span><strong>{lien.file_number}</strong></div>}
              <div className="cf-modal-row">
                <span>Original Lien</span>
                <input
                  type="number"
                  step="0.01"
                  value={originalLien}
                  onChange={(e) => setOriginalLien(e.target.value)}
                  style={{ width: '140px', textAlign: 'right', padding: '0.3rem 0.5rem' }}
                />
              </div>
            </div>

            <div className="cf-modal-section">
              <div className="cf-modal-section-title">Case Inputs</div>
              <div className="cf-modal-inputs">
                <label>Settlement Amount ($)
                  <input type="number" step="0.01" value={settlement} onChange={(e) => setSettlement(e.target.value)} />
                </label>
                <label>Attorney Fees ($)
                  <input type="number" step="0.01" value={fees} onChange={(e) => setFees(e.target.value)} />
                </label>
                <label>Costs ($)
                  <input type="number" step="0.01" value={costs} onChange={(e) => setCosts(e.target.value)} />
                </label>
                <label>Recipient Email
                  <input type="email" value={recipientEmail} onChange={(e) => setRecipientEmail(e.target.value)} />
                </label>
              </div>
            </div>

            <div className="cf-calc">
              <div className="cf-calc-line">
                <span>Fees + Costs</span>
                <span>{formatCurrency(numFees)} + {formatCurrency(numCosts)} = <strong>{formatCurrency(totalFeesCosts)}</strong></span>
              </div>
              <div className="cf-calc-line">
                <span>Common Fund %</span>
                <span>{formatCurrency(totalFeesCosts)} / {formatCurrency(numSettlement)} = <strong>{(pct * 100).toFixed(2)}%</strong></span>
              </div>
              <div className="cf-calc-line">
                <span>Reduction</span>
                <span>{formatCurrency(numOriginal)} &minus; ({formatCurrency(numOriginal)} &times; {(pct * 100).toFixed(2)}%)</span>
              </div>
              <div className="cf-calc-line cf-calc-final">
                <span>Reduced Lien</span>
                <span>{formatCurrency(reduced)}</span>
              </div>
            </div>

            {error && <div style={{ color: '#dc2626', marginTop: '0.75rem', fontSize: '0.9rem' }}>{error}</div>}

            <div className="cf-modal-actions">
              <button type="button" className="btn-secondary" onClick={close}>Cancel</button>
              <button
                type="button"
                className="btn-primary"
                disabled={!canSend}
                onClick={send}
              >
                {sending ? 'Sending…' : 'Send Common Fund Email'}
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  };

  const ProjectModal = () => {
    const [projectDetails, setProjectDetails] = useState(null);
    const [loadingDetails, setLoadingDetails] = useState(false);
    const [insurancePayments, setInsurancePayments] = useState({}); // Track insurance payment edits
    const [originalBills, setOriginalBills] = useState({}); // Track original-bill override edits
    const [finalAmounts, setFinalAmounts] = useState({}); // Track final-amount override edits
    const [lienClaimNumbers, setLienClaimNumbers] = useState({}); // Track Claim # edits on Liens tab
    const [savingLien, setSavingLien] = useState(null); // lien id currently saving
    // Final lien amount — what the settlement letter pays this lienholder.
    // Separate from the negotiated figure: a negotiation can settle at one
    // number and the final lien land at another.
    const [lienFinalAmounts, setLienFinalAmounts] = useState({});
    const [savingMedication, setSavingMedication] = useState(null); // Track which medication is being saved
    const [statuses, setStatuses] = useState({}); // Track status edits
    const [refreshingProject, setRefreshingProject] = useState(false); // Project-detail "refresh from Filevine" state
    const [emailModal, setEmailModal] = useState(null); // Track which medication's email modal is open {medicationId, emails}
    const [selectedEmail, setSelectedEmail] = useState('');
    // Multi-recipient selection for the Start Negotiation flow (task #4).
    // We keep selectedEmail as the "primary" for backward compat with the
    // ad-hoc send-email tab, and additionally track a Set of checked
    // addresses that all get included in the negotiation send.
    const [selectedEmails, setSelectedEmails] = useState(() => new Set());
    const [newEmailInput, setNewEmailInput] = useState(''); // Free-text input for adding additional emails
    const [savingNewEmail, setSavingNewEmail] = useState(false);
    const [emailSubject, setEmailSubject] = useState('');
    const [emailBody, setEmailBody] = useState('');
    const [sendingEmail, setSendingEmail] = useState(false);
    const [emailMode, setEmailMode] = useState('email');
    const [negotiationForm, setNegotiationForm] = useState({
      legal_costs: '',
      attorney_fees_percentage: '40',
      requested_amount: '',
      settlement_amount: '',
      settlement_confidential: false,
    });
    const [startingNegotiation, setStartingNegotiation] = useState(false);

    // Sub-tab navigation on the project detail modal (task #24).
    // 'meds' = existing meds table, 'liens' = healthinslien from Filevine,
    // 'insurance' = Med Pay rows from the insurance collection.
    const [activeProjectTab, setActiveProjectTab] = useState('meds');
    const [liensData, setLiensData] = useState(null);
    const [liensLoading, setLiensLoading] = useState(false);
    const [insuranceData, setInsuranceData] = useState(null);
    const [insuranceLoading, setInsuranceLoading] = useState(false);
    // Common Fund modal (task #22). State carries the lien being negotiated
    // and the user-entered settlement/fees/costs inputs.
    const [commonFundModal, setCommonFundModal] = useState(null);
    const [madeWholeModal, setMadeWholeModal] = useState(null); // {lienId, lien, projectId}
    // Med Pay reimbursement: letters + negotiation for one insurance row.
    const [medPayModal, setMedPayModal] = useState(null); // {ins, projectId}
    const [sendingCommonFund, setSendingCommonFund] = useState(false);

    const loadProjectDetails = useCallback(async (projectId) => {
      setLoadingDetails(true);
      try {
        const response = await axios.get(`${API_URL}/api/projects/${projectId}`, { withCredentials: true });
        setProjectDetails(response.data);
      } catch (error) {
        console.error('Error loading project details:', error);
      } finally {
        setLoadingDetails(false);
      }
    }, []);

    // Lazy-load the Liens and Insurance tabs on demand so opening a project
    // doesn't fire three Filevine collection calls when the user only wants meds.
    const loadLiens = useCallback(async (projectId) => {
      setLiensLoading(true);
      try {
        const r = await axios.get(`${API_URL}/api/projects/${projectId}/liens`, { withCredentials: true });
        setLiensData(r.data);
      } catch (e) {
        console.error('Error loading liens:', e);
        setLiensData({ items: [], count: 0, error: e.response?.data?.detail || e.message });
      } finally {
        setLiensLoading(false);
      }
    }, []);

    const loadInsurance = useCallback(async (projectId) => {
      setInsuranceLoading(true);
      try {
        const r = await axios.get(`${API_URL}/api/projects/${projectId}/insurance`, { withCredentials: true });
        setInsuranceData(r.data);
      } catch (e) {
        console.error('Error loading insurance:', e);
        setInsuranceData({ items: [], count: 0, error: e.response?.data?.detail || e.message });
      } finally {
        setInsuranceLoading(false);
      }
    }, []);

    useEffect(() => {
      if (!selectedProject?.id) return;
      if (activeProjectTab === 'liens' && liensData === null) loadLiens(selectedProject.id);
      if (activeProjectTab === 'insurance' && insuranceData === null) loadInsurance(selectedProject.id);
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [activeProjectTab, selectedProject?.id]);

    useEffect(() => {
      if (selectedProject?.id) {
        loadProjectDetails(selectedProject.id);
      }
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [selectedProject?.id]);

    if (!selectedProject) return null;

    const displayProject = projectDetails || selectedProject;

    const handleInsurancePaymentChange = (medicationId, value) => {
      setInsurancePayments(prev => ({
        ...prev,
        [medicationId]: value
      }));
    };

    const handleOriginalBillChange = (medicationId, value) => {
      setOriginalBills(prev => ({
        ...prev,
        [medicationId]: value
      }));
    };

    const handleFinalAmountChange = (medicationId, value) => {
      setFinalAmounts(prev => ({ ...prev, [medicationId]: value }));
    };

    const handleLienFinalChange = (lienId, value) => {
      setLienFinalAmounts(prev => ({ ...prev, [lienId]: value }));
    };

    const handleLienFinalSave = async (lienId) => {
      setSavingLien(lienId);
      try {
        const raw = lienFinalAmounts[lienId];
        await axios.patch(
          `${API_URL}/api/projects/${selectedProject.id}/liens/${lienId}`,
          { final_lien_amount: raw === '' || raw === undefined ? null : Number(raw) },
          { withCredentials: true }
        );
        setLienFinalAmounts(prev => {
          const next = { ...prev };
          delete next[lienId];
          return next;
        });
        await loadLiens(selectedProject.id);
      } catch (error) {
        console.error('Error saving final lien amount:', error);
        alert('Failed to save the final lien amount. ' + (error.response?.data?.detail || error.message || ''));
      } finally {
        setSavingLien(null);
      }
    };

    const handleLienClaimChange = (lienId, value) => {
      setLienClaimNumbers(prev => ({ ...prev, [lienId]: value }));
    };

    /**
     * Save a manually-entered Claim # against a lien (task #43).
     * Persists to project_overlay under liens.<id>.claim_number_override.
     * Empty clears the override and falls back to Filevine's value.
     */
    const handleLienClaimSave = async (lienId) => {
      setSavingLien(lienId);
      try {
        await axios.patch(
          `${API_URL}/api/projects/${selectedProject.id}/liens/${lienId}`,
          { claim_number: lienClaimNumbers[lienId] ?? '' },
          { withCredentials: true }
        );
        setLienClaimNumbers(prev => {
          const next = { ...prev };
          delete next[lienId];
          return next;
        });
        // Refresh the liens list so the saved value renders from source.
        await loadLiens(selectedProject.id);
      } catch (error) {
        console.error('Error saving claim number:', error);
        alert('Failed to save the claim number. ' + (error.response?.data?.detail || error.message || ''));
      } finally {
        setSavingLien(null);
      }
    };

    /**
     * Save a manual Final Amount override.
     *
     * Filevine frequently leaves its final-amount field blank, and because
     * Lien Amount is computed as (final - insurance paid), a blank final
     * meant BOTH columns rendered N/A with no way to correct them. Saving
     * a value here populates Final Amount and makes Lien Amount compute.
     * Empty string clears the override and falls back to Filevine.
     */
    const handleFinalAmountSave = async (medicationId) => {
      setSavingMedication(medicationId);
      try {
        const raw = finalAmounts[medicationId];
        const bodyValue = raw === '' ? null : parseFloat(raw);
        await axios.put(
          `${API_URL}/api/projects/${selectedProject.id}/medication/${medicationId}`,
          { final_amount: bodyValue },
          { withCredentials: true }
        );
        setFinalAmounts(prev => {
          const next = { ...prev };
          delete next[medicationId];
          return next;
        });
        // Re-fetch so the computed Lien Amount reflects the new final amount.
        const updated = await axios.get(
          `${API_URL}/api/projects/${selectedProject.id}`,
          { withCredentials: true }
        );
        setProjectDetails(updated.data);
      } catch (error) {
        console.error('Error saving final amount override:', error);
        alert('Failed to save the final amount. ' + (error.response?.data?.detail || error.message || ''));
      } finally {
        setSavingMedication(null);
      }
    };

    const handleOriginalBillSave = async (medicationId) => {
      setSavingMedication(medicationId);
      try {
        const raw = originalBills[medicationId];
        // Empty string → null clears the override and the displayed value falls back to Filevine.
        const bodyValue = raw === '' ? null : parseFloat(raw);
        await axios.put(
          `${API_URL}/api/projects/${selectedProject.id}/medication/${medicationId}`,
          { original_bill: bodyValue },
          { withCredentials: true }
        );
        // Drop the dirty marker for this row.
        setOriginalBills(prev => {
          const next = { ...prev };
          delete next[medicationId];
          return next;
        });
        // Re-fetch project so the displayed Original Bill / Final Amount reflect the change.
        const updated = await axios.get(
          `${API_URL}/api/projects/${selectedProject.id}`,
          { withCredentials: true }
        );
        setProjectDetails(updated.data);
      } catch (error) {
        console.error('Error saving original bill override:', error);
        alert('Failed to save the original bill. ' + (error.response?.data?.detail || error.message || ''));
      } finally {
        setSavingMedication(null);
      }
    };

    const handleRefreshFromFilevine = async () => {
      if (!selectedProject?.id || refreshingProject) return;
      setRefreshingProject(true);
      try {
        await axios.post(
          `${API_URL}/api/projects/${selectedProject.id}/refresh`,
          {},
          { withCredentials: true }
        );
        await loadProjectDetails(selectedProject.id);
      } catch (error) {
        console.error('Error refreshing project from Filevine:', error);
        alert('Refresh failed. ' + (error.response?.data?.detail || error.message || ''));
      } finally {
        setRefreshingProject(false);
      }
    };

    const handleInsurancePaymentSave = async (medicationId) => {
      setSavingMedication(medicationId);
      try {
        const insurancePayment = insurancePayments[medicationId];
        await axios.put(
          `${API_URL}/api/projects/${selectedProject.id}/medication/${medicationId}`,
          { insurance_payment: insurancePayment || null },
          { withCredentials: true }
        );
        const updated = await axios.get(`${API_URL}/api/projects/${selectedProject.id}`, { withCredentials: true });
        setProjectDetails(updated.data);
        setSelectedProject(updated.data);
        const newPayments = { ...insurancePayments };
        delete newPayments[medicationId];
        setInsurancePayments(newPayments);
      } catch (error) {
        console.error('Error saving insurance payment:', error);
        alert('Failed to save insurance payment. Please try again.');
      } finally {
        setSavingMedication(null);
      }
    };

    const handleStatusChange = (medicationId, value) => {
      setStatuses(prev => ({
        ...prev,
        [medicationId]: value
      }));
    };

    const handleStatusSave = async (medicationId) => {
      setSavingMedication(medicationId);
      try {
        const status = statuses[medicationId];
        await axios.put(
          `${API_URL}/api/projects/${selectedProject.id}/medication/${medicationId}`,
          { status: status || null },
          { withCredentials: true }
        );
        const updated = await axios.get(`${API_URL}/api/projects/${selectedProject.id}`, { withCredentials: true });
        setProjectDetails(updated.data);
        setSelectedProject(updated.data);
        const newStatuses = { ...statuses };
        delete newStatuses[medicationId];
        setStatuses(newStatuses);
      } catch (error) {
        console.error('Error saving status:', error);
        alert('Failed to save status. Please try again.');
      } finally {
        setSavingMedication(null);
      }
    };

    const openEmailModalWithEmails = (medicationId, emailsList) => {
      const normalizedEmails = emailsList.map(email =>
        typeof email === 'string' ? { email, notes: null } : email
      );
      setEmailModal({ medicationId, emails: normalizedEmails });
      // When there are no emails, the modal shows the "Add email" form instead of
      // the dropdown — leave selectedEmail empty until the user saves one.
      setSelectedEmail(normalizedEmails.length > 0 ? normalizedEmails[0].email : '');
      // Seed the multi-select checkbox state: by default the first known
      // email is checked. Team can toggle or add more before sending.
      const seed = new Set();
      if (normalizedEmails.length > 0) seed.add(normalizedEmails[0].email);
      setSelectedEmails(seed);
      setNewEmailInput('');
      setEmailMode('email');
      const med = displayProject.medications?.find(m => m.id === medicationId);
      // Pre-fill from case-level defaults + med lien amount. Case defaults
      // save the attorney from re-typing settlement / fees / costs on every
      // negotiation for the same project.
      const caseDefaults = displayProject.case_negotiation_defaults || {};
      setNegotiationForm(prev => ({
        ...prev,
        legal_costs: caseDefaults.legal_costs != null
          ? String(caseDefaults.legal_costs)
          : prev.legal_costs,
        attorney_fees_percentage: caseDefaults.attorney_fees_pct != null
          ? String(caseDefaults.attorney_fees_pct)
          : prev.attorney_fees_percentage,
        settlement_amount: caseDefaults.settlement_amount != null
          ? String(caseDefaults.settlement_amount)
          : prev.settlement_amount,
        settlement_confidential: caseDefaults.settlement_confidential ?? prev.settlement_confidential,
        requested_amount: (med && med.lien_amount)
          ? med.lien_amount.toFixed(2)
          : prev.requested_amount,
      }));
    };

    const handleEmailIconClick = async (medicationId, providerEmails) => {
      if (providerEmails && Array.isArray(providerEmails) && providerEmails.length > 0) {
        openEmailModalWithEmails(medicationId, providerEmails);
        return;
      }
      try {
        const response = await axios.get(
          `${API_URL}/api/projects/${selectedProject.id}/medication/${medicationId}/emails`,
          { withCredentials: true }
        );
        const emails = response.data?.emails || [];
        // Always open the modal — when emails is empty, the modal renders an
        // "Add provider email" form so the user can enter one manually.
        openEmailModalWithEmails(medicationId, emails);
      } catch (error) {
        console.error('Error fetching provider emails:', error);
        // Still open the modal so the user can add an email manually if Filevine
        // is unreachable or the provider record is empty.
        openEmailModalWithEmails(medicationId, []);
      }
    };

    const handleSaveNewEmail = async () => {
      if (!emailModal || !newEmailInput.trim()) return;
      const addr = newEmailInput.trim();
      if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(addr)) {
        alert('That doesn’t look like a valid email address. Try again.');
        return;
      }
      setSavingNewEmail(true);
      try {
        // Merge with any existing overlay emails so we don't drop previously-added ones.
        const existingOverlay = emailModal.emails
          .filter(e => (typeof e === 'object' ? e.notes === 'added in liens app' : false))
          .map(e => (typeof e === 'string' ? e : e.email));
        const combined = Array.from(new Set([...existingOverlay, addr]));
        await axios.put(
          `${API_URL}/api/projects/${selectedProject.id}/medication/${emailModal.medicationId}`,
          { provider_emails_override: combined },
          { withCredentials: true }
        );
        // Optimistically add to the modal's list so the user can continue without reloading.
        const newEntry = { email: addr, notes: 'added in liens app' };
        setEmailModal(prev => prev ? { ...prev, emails: [...prev.emails, newEntry] } : prev);
        setSelectedEmail(addr);
        // Auto-check the newly added email so it's included on send (task #4)
        setSelectedEmails(prev => {
          const next = new Set(prev);
          next.add(addr);
          return next;
        });
        setNewEmailInput('');
      } catch (error) {
        console.error('Error saving provider email override:', error);
        alert('Failed to save the email. ' + (error.response?.data?.detail || error.message || ''));
      } finally {
        setSavingNewEmail(false);
      }
    };

    const handleStartNegotiation = async () => {
      if (!selectedEmail || !emailModal) {
        alert('Please select an email address.');
        return;
      }
      
      if (!negotiationForm.legal_costs || !negotiationForm.attorney_fees_percentage || !negotiationForm.requested_amount) {
        alert('Please fill in all negotiation fields.');
        return;
      }
      
      setStartingNegotiation(true);
      try {
        // Multi-recipient (task #4): send the full Set of checked emails.
        // Falls back to selectedEmail for legacy / single-select cases.
        const recipientArr = Array.from(selectedEmails).filter(Boolean);
        if (recipientArr.length === 0 && !selectedEmail) {
          alert('Please select at least one recipient email before sending.');
          setStartingNegotiation(false);
          return;
        }
        const finalRecipients = recipientArr.length > 0 ? recipientArr : [selectedEmail];
        const response = await axios.post(
          `${API_URL}/api/projects/${selectedProject.id}/medication/${emailModal.medicationId}/start-negotiation`,
          {
            recipient_email: finalRecipients[0],          // backward compat
            recipient_emails: finalRecipients,            // new array field
            legal_costs: parseFloat(negotiationForm.legal_costs),
            attorney_fees_percentage: parseFloat(negotiationForm.attorney_fees_percentage),
            requested_amount: parseFloat(negotiationForm.requested_amount),
            settlement_amount: negotiationForm.settlement_amount
              ? parseFloat(negotiationForm.settlement_amount)
              : null,
            settlement_confidential: !!negotiationForm.settlement_confidential,
          },
          { withCredentials: true }
        );
        
        // Check if an existing negotiation was returned
        if (response.data.existing) {
          alert('An active negotiation already exists for this med. Opening existing negotiation...');
          
          // Close modal
          setEmailModal(null);
          setSelectedEmail('');
          setEmailMode('email');
          setNegotiationForm({ legal_costs: '', attorney_fees_percentage: '40', requested_amount: '', settlement_amount: '', settlement_confidential: false });
          
          // Switch to negotiations tab and load the existing negotiation
          setActiveTab('negotiations');
          negotiationsLoadedRef.current = false;
          await loadNegotiations();
          
          // Try to find and select the existing negotiation
          if (response.data.negotiation_id) {
            try {
              const negResponse = await axios.get(`${API_URL}/api/negotiations/${response.data.negotiation_id}`, { withCredentials: true });
              setSelectedNegotiation(negResponse.data);
            } catch (error) {
              console.error('Error loading existing negotiation:', error);
            }
          }
        } else {
        alert('Negotiation email sent successfully!');
        
        // Close modal and reload project details
        setEmailModal(null);
        setSelectedEmail('');
        setEmailMode('email');
        setNegotiationForm({ legal_costs: '', attorney_fees_percentage: '40', requested_amount: '', settlement_amount: '', settlement_confidential: false });
        await loadProjectDetails(selectedProject.id);
        // Refresh negotiations list
        await loadNegotiations();
        }
      } catch (error) {
        console.error('Error starting negotiation:', error);
        alert(error.response?.data?.detail || 'Failed to start negotiation. Please try again.');
      } finally {
        setStartingNegotiation(false);
      }
    };

    const handleSendEmail = async () => {
      if (!selectedEmail || !emailModal) {
        alert('Please select an email address.');
        return;
      }
      
      if (!emailSubject.trim() || !emailBody.trim()) {
        alert('Please fill in both subject and body.');
        return;
      }
      
      setSendingEmail(true);
      try {
        await axios.post(
          `${API_URL}/api/projects/${selectedProject.id}/medication/${emailModal.medicationId}/send-email`,
          {
            recipient_email: selectedEmail,
            subject: emailSubject,
            body: emailBody
          },
          { withCredentials: true }
        );
        
        alert('Email sent successfully!');
        setEmailModal(null);
        setSelectedEmail('');
        setEmailSubject('');
        setEmailBody('');
      } catch (error) {
        console.error('Error sending email:', error);
        alert(error.response?.data?.detail || 'Failed to send email. Please try again.');
      } finally {
        setSendingEmail(false);
      }
    };

    return (
      <div className="modal-overlay" onClick={() => setSelectedProject(null)}>
        <div className="modal-content" onClick={(e) => e.stopPropagation()}>
          <div className="modal-header">
            <h2>{displayProject.client_name}</h2>
            <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
              <button
                onClick={handleRefreshFromFilevine}
                disabled={refreshingProject || loadingDetails}
                className="btn-refresh"
                title="Pull fresh data from Filevine for this case"
                style={{
                  background: 'transparent',
                  border: '1px solid var(--text-secondary, #888)',
                  borderRadius: '4px',
                  padding: '0.25rem 0.75rem',
                  cursor: refreshingProject ? 'wait' : 'pointer',
                  fontSize: '0.875rem',
                  color: 'var(--text-secondary, #555)',
                }}
              >
                {refreshingProject ? 'Refreshing…' : '↻ Refresh from Filevine'}
              </button>
              <button onClick={() => setSelectedProject(null)} className="btn-close">×</button>
            </div>
          </div>
          
          <div className="modal-body">
            {loadingDetails ? (
              <p>Loading project details...</p>
            ) : (
              <>
                <div className="project-info">
                  <p>
                    <strong>Client</strong>
                    <span>{displayProject.client_name}</span>
                  </p>
                  <p>
                    <strong>Incident Date</strong>
                    <span>{formatDateOnly(displayProject.incident_date)}</span>
                  </p>
                  <p>
                    <strong>Status</strong>
                    <span>{displayProject.phase_name || 'N/A'}</span>
                  </p>
                </div>

                <div className="totals-summary">
                  <h3>Financial Summary</h3>
                  <table className="financial-summary-table">
                    <thead>
                      <tr>
                        <th>METRIC</th>
                        <th>AMOUNT</th>
                      </tr>
                    </thead>
                    <tbody>
                      <tr>
                        <td>Total Original Bills (Confirmed)</td>
                        <td>{formatCurrency(displayProject.total_original_bills_confirmed || 0)}</td>
                      </tr>
                      <tr>
                        <td>Total Final Amounts</td>
                        <td>{formatCurrency(displayProject.total_final_amounts || 0)}</td>
                      </tr>
                      <tr>
                        <td>Total Savings</td>
                        <td>{formatCurrency(displayProject.total_savings || 0)}</td>
                      </tr>
                      <tr>
                        <td>Total Original Bills (Pending)</td>
                        <td>{formatCurrency(displayProject.total_original_bills_pending || 0)}</td>
                      </tr>
                      <tr>
                        <td>Total Insurance Payments</td>
                        <td>{formatCurrency(displayProject.total_insurance_payments || 0)}</td>
                      </tr>
                      <tr>
                        <td>Total Lien Amounts</td>
                        <td>{formatCurrency(displayProject.total_lien_amounts || 0)}</td>
                      </tr>
                      {/* Show the Liens-tab portion separately so the team
                          can see how much of the total comes from health
                          insurance subrogators (task #3). Only render the
                          row when there's a non-zero amount. */}
                      {(displayProject.total_health_insurance_liens_original || 0) > 0 && (
                        <tr>
                          <td style={{ paddingLeft: '1.5rem', color: 'var(--text-secondary, #6b7280)', fontSize: '0.92rem' }}>
                            &nbsp;&nbsp;of which Health Insurance Liens (Original)
                          </td>
                          <td style={{ color: 'var(--text-secondary, #6b7280)', fontSize: '0.92rem' }}>
                            {formatCurrency(displayProject.total_health_insurance_liens_original || 0)}
                          </td>
                        </tr>
                      )}
                    </tbody>
                  </table>
                </div>

                {/* Case Financials strip — settlement/fees/costs saved once
                    per project, auto-fills every negotiation on this case.
                    Lives ABOVE the tabs so it's visible whether you're on
                    Meds, Liens, Insurance, or MayLogic. Also mirrored inside
                    the MayLogic Scratchpad; edits sync via project_overlay. */}
                <CaseFinancials
                  projectId={selectedProject?.id}
                  initialValues={displayProject.case_negotiation_defaults}
                  onSaved={(updated) => {
                    // Reflect the save locally so downstream components
                    // (Scratchpad, negotiation modal) see the fresh values.
                    setProjectDetails(prev => prev ? {
                      ...prev,
                      case_negotiation_defaults: updated,
                    } : prev);
                  }}
                />

                <div className="project-tabs" role="tablist">
                  <button
                    type="button"
                    role="tab"
                    aria-selected={activeProjectTab === 'meds'}
                    className={`project-tab${activeProjectTab === 'meds' ? ' active' : ''}`}
                    onClick={() => setActiveProjectTab('meds')}
                  >
                    Meds <span className="project-tab-count">{displayProject.medications_count || 0}</span>
                  </button>
                  <button
                    type="button"
                    role="tab"
                    aria-selected={activeProjectTab === 'liens'}
                    className={`project-tab${activeProjectTab === 'liens' ? ' active' : ''}`}
                    onClick={() => setActiveProjectTab('liens')}
                  >
                    Liens {liensData && <span className="project-tab-count">{liensData.count || 0}</span>}
                  </button>
                  <button
                    type="button"
                    role="tab"
                    aria-selected={activeProjectTab === 'insurance'}
                    className={`project-tab${activeProjectTab === 'insurance' ? ' active' : ''}`}
                    onClick={() => setActiveProjectTab('insurance')}
                  >
                    Insurance (Med Pay) {insuranceData && <span className="project-tab-count">{insuranceData.count || 0}</span>}
                  </button>
                  {/* Letters tab (task #44) — sits between Insurance and
                      MayLogic per Teresa's placement. */}
                  <button
                    type="button"
                    role="tab"
                    aria-selected={activeProjectTab === 'letters'}
                    className={`project-tab${activeProjectTab === 'letters' ? ' active' : ''}`}
                    onClick={() => setActiveProjectTab('letters')}
                  >
                    Letters <span className="project-tab-chip">New</span>
                  </button>
                  <button
                    type="button"
                    role="tab"
                    aria-selected={activeProjectTab === 'maylogic'}
                    className={`project-tab${activeProjectTab === 'maylogic' ? ' active' : ''}`}
                    onClick={() => setActiveProjectTab('maylogic')}
                  >
                    MayLogic <span className="project-tab-chip">New</span>
                  </button>
                </div>

                {activeProjectTab === 'meds' && (
                <div className="medications-section">
                  <h3>Meds ({displayProject.medications_count || 0})</h3>
                  <div className="medications-table">
                    <table>
                    <thead>
                        <tr>
                          <th>Date</th>
                          <th>Provider</th>
                          <th style={{ textAlign: 'right' }}>Original Bill</th>
                          <th style={{ textAlign: 'right' }}>Insurance Paid</th>
                          <th style={{ textAlign: 'right' }}>Lien Amount</th>
                          <th style={{ textAlign: 'right' }}>Final Amount</th>
                          <th>Status</th>
                          <th>Negotiation</th>
                          <th style={{ textAlign: 'center' }}>Email</th>
                      </tr>
                    </thead>
                    <tbody>
                      {displayProject.medications && displayProject.medications.length > 0 ? (
                        displayProject.medications.map((med) => {
                          const currentInsurancePayment = insurancePayments[med.id] !== undefined
                            ? insurancePayments[med.id]
                            : (med.insurance_payment || '');
                          const hasUnsavedChanges = insurancePayments[med.id] !== undefined;
                          const isSaving = savingMedication === med.id;
                          const currentStatus = statuses[med.id] !== undefined
                            ? statuses[med.id]
                            : (med.status || '');
                          const hasUnsavedStatus = statuses[med.id] !== undefined;
                          const currentOriginalBill = originalBills[med.id] !== undefined
                            ? originalBills[med.id]
                            : (med.original_bill !== null && med.original_bill !== undefined ? String(med.original_bill) : '');
                          const hasUnsavedOriginalBill = originalBills[med.id] !== undefined;
                          const currentFinalAmount = finalAmounts[med.id] !== undefined
                            ? finalAmounts[med.id]
                            : (med.final_amount !== null && med.final_amount !== undefined ? String(med.final_amount) : '');
                          const hasUnsavedFinalAmount = finalAmounts[med.id] !== undefined;

                          return (
                            <tr key={med.id} style={{ borderBottom: '1px solid #ddd' }}>
                              <td>
                                {formatDateOnly(med.date)}
                              </td>
                              <td>{med.provider_name || 'Unknown'}</td>
                              <td style={{ textAlign: 'right' }}>
                                <input
                                  type="number"
                                  step="0.01"
                                  min="0"
                                  value={currentOriginalBill}
                                  onChange={(e) => handleOriginalBillChange(med.id, e.target.value)}
                                  onBlur={() => {
                                    if (hasUnsavedOriginalBill) {
                                      handleOriginalBillSave(med.id);
                                    }
                                  }}
                                  placeholder="0.00"
                                  className={`medication-input ${hasUnsavedOriginalBill ? 'unsaved' : ''}`}
                                  disabled={isSaving}
                                  title="Edit if the Filevine value is incorrect; saved as an override in the liens app"
                                />
                              </td>
                              <td style={{ textAlign: 'right' }}>
                                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-end', gap: '0.5rem' }}>
                                  <input
                                    type="number"
                                    step="0.01"
                                    min="0"
                                    value={currentInsurancePayment}
                                    onChange={(e) => handleInsurancePaymentChange(med.id, e.target.value)}
                                    onBlur={() => {
                                      if (hasUnsavedChanges) {
                                        handleInsurancePaymentSave(med.id);
                                      }
                                    }}
                                    placeholder="0.00"
                                    className={`medication-input ${hasUnsavedChanges ? 'unsaved' : ''}`}
                                    disabled={isSaving}
                                  />
                                  {isSaving && <span style={{ fontSize: '0.75rem', color: 'var(--text-secondary)' }}>Saving...</span>}
                                </div>
                              </td>
                              {/* Lien Amount stays computed (final − insurance paid).
                                  It shows a hint instead of a bare N/A so it's
                                  obvious WHY it's empty and how to fill it. */}
                              <td style={{ textAlign: 'right' }}>
                                {med.lien_amount !== null && med.lien_amount !== undefined
                                  ? formatCurrency(med.lien_amount)
                                  : (
                                    <span
                                      style={{ color: 'var(--text-secondary, #9ca3af)', fontSize: '0.85em' }}
                                      title="Computed as Final Amount minus Insurance Paid. Enter a Final Amount to populate this."
                                    >
                                      —
                                    </span>
                                  )}
                              </td>
                              {/* Final Amount is manually editable — Filevine
                                  often leaves it blank, which also blanked the
                                  computed Lien Amount above. */}
                              <td style={{ textAlign: 'right' }}>
                                <input
                                  type="text"
                                  inputMode="decimal"
                                  value={currentFinalAmount}
                                  onChange={(e) => {
                                    const clean = sanitizeDecimalInput(e.target.value);
                                    if (clean !== null) handleFinalAmountChange(med.id, clean);
                                  }}
                                  onBlur={() => {
                                    if (hasUnsavedFinalAmount) handleFinalAmountSave(med.id);
                                  }}
                                  placeholder="0.00"
                                  className={`medication-input ${hasUnsavedFinalAmount ? 'unsaved' : ''}`}
                                  disabled={isSaving}
                                  title="Filevine's final amount, or enter one manually if Filevine has none. Lien Amount computes from this."
                                />
                              </td>
                              <td>
                                <select
                                  value={currentStatus}
                                  onChange={(e) => handleStatusChange(med.id, e.target.value)}
                                  onBlur={() => {
                                    if (hasUnsavedStatus) {
                                      handleStatusSave(med.id);
                                    }
                                  }}
                                  className={`medication-select ${hasUnsavedStatus ? 'unsaved' : ''}`}
                                  disabled={isSaving}
                                >
                                  <option value="">Select Status</option>
                                  <option value="Pending">Pending</option>
                                  <option value="Negotiating">Negotiating</option>
                                  <option value="Resolved">Resolved</option>
                                </select>
                              </td>
                              {/* Inline negotiation cell (task #7) — shows the
                                  active negotiation's status + amount with a
                                  View link, or "—" if none. '+N prior' is
                                  shown when there are withdrawn/rejected
                                  historical negotiations on this med. */}
                              <td>
                                {med.active_negotiation ? (
                                  <div className="med-neg-cell">
                                    <div className="med-neg-row med-neg-row-1">
                                      <span className={`med-neg-dot dot-status-${med.active_negotiation.status}`} />
                                      <span className="med-neg-status">
                                        {med.active_negotiation.status.replace('_', ' ')}
                                      </span>
                                    </div>
                                    <div className="med-neg-row med-neg-row-2">
                                      <span className="med-neg-amount">
                                        {formatCurrency(med.active_negotiation.current_amount || 0)}
                                      </span>
                                      <button
                                        type="button"
                                        className="med-neg-view"
                                        onClick={async () => {
                                          try {
                                            const resp = await axios.get(
                                              `${API_URL}/api/negotiations/${med.active_negotiation.id}`,
                                              { withCredentials: true }
                                            );
                                            setSelectedNegotiation(resp.data);
                                            setActiveTab('negotiations');
                                            setSelectedProject(null);
                                          } catch (err) {
                                            alert('Failed to open negotiation: ' + (err.response?.data?.detail || err.message));
                                          }
                                        }}
                                        title="Open this negotiation"
                                      >
                                        view
                                      </button>
                                      {(med.prior_negotiations_count || 0) > 0 && (
                                        <span className="med-neg-prior" title={`${med.prior_negotiations_count} withdrawn/rejected negotiation(s)`}>
                                          +{med.prior_negotiations_count} prior
                                        </span>
                                      )}
                                    </div>
                                  </div>
                                ) : (med.prior_negotiations_count || 0) > 0 ? (
                                  <span className="med-neg-prior" title="All prior negotiations withdrawn or rejected">
                                    {med.prior_negotiations_count} prior
                                  </span>
                                ) : (
                                  <span style={{ color: 'var(--text-secondary, #6b7280)' }}>—</span>
                                )}
                              </td>
                              <td style={{ textAlign: 'center' }}>
                                <button
                                  type="button"
                                  onClick={(e) => {
                                    // Prevent any default browser behavior (focus/scroll-into-view)
                                    // that was causing the page to jump on the first click.
                                    e.preventDefault();
                                    e.stopPropagation();
                                    handleEmailIconClick(med.id, med.provider_emails);
                                  }}
                                  className="email-icon-btn"
                                  title={med.provider_emails && Array.isArray(med.provider_emails) && med.provider_emails.length > 0
                                    ? `Email provider (${med.provider_emails.length} email${med.provider_emails.length > 1 ? 's' : ''} available)`
                                    : 'No provider email on file — click to add one'}
                                  style={{
                                    visibility: 'visible',
                                    display: 'inline-flex'
                                  }}
                                >
                                  ✉
                                </button>
                              </td>
                            </tr>
                          );
                        })
                      ) : (
                        <tr>
                          <td colSpan="9" style={{ padding: '2rem', textAlign: 'center', color: 'var(--text-secondary)' }}>No meds found</td>
                        </tr>
                      )}
                    </tbody>
                  </table>
                  </div>
                </div>
                )}

                {activeProjectTab === 'liens' && (
                  <div className="medications-section">
                    <h3>Liens {liensData && `(${liensData.count || 0})`}</h3>
                    {liensLoading && <p style={{ padding: '1rem', color: 'var(--text-secondary)' }}>Loading liens…</p>}
                    {!liensLoading && liensData?.error && (
                      <p style={{ padding: '1rem', color: '#dc2626' }}>Could not load liens: {liensData.error}</p>
                    )}
                    {!liensLoading && liensData && !liensData.error && (
                      <div className="medications-table">
                        <table>
                          <thead>
                            <tr>
                              <th>Lienholder</th>
                              <th>Subrogation Co.</th>
                              <th>Adjuster</th>
                              <th>File #</th>
                              {/* Task #43: Claim # sits right after File #.
                                  Editable because Filevine often has neither
                                  claimnumber nor identificationnumber set. */}
                              <th>Claim #</th>
                              <th style={{ textAlign: 'right' }}>Original Lien</th>
                              <th style={{ textAlign: 'right' }}>Current Lien</th>
                              <th style={{ textAlign: 'right' }} title="The figure that goes on the settlement letter">Final Lien</th>
                              <th>Negotiation</th>
                              <th>Notes</th>
                              <th style={{ textAlign: 'center' }}>Action</th>
                            </tr>
                          </thead>
                          <tbody>
                            {liensData.items?.length > 0 ? liensData.items.map((lien) => (
                              <tr key={lien.id}>
                                <td><strong>{lien.lienholder_name}</strong></td>
                                <td>{lien.subrogation_company || '—'}</td>
                                <td>{lien.adjuster_name || '—'}</td>
                                <td>{lien.file_number || '—'}</td>
                                {/* Claim # — editable, save-on-blur. Blank in
                                    Filevine on most liens, so the team types
                                    it here and it persists in the overlay. */}
                                <td>
                                  <input
                                    type="text"
                                    value={
                                      lienClaimNumbers[lien.id] !== undefined
                                        ? lienClaimNumbers[lien.id]
                                        : (lien.claim_number || '')
                                    }
                                    onChange={(e) => handleLienClaimChange(lien.id, e.target.value)}
                                    onBlur={() => {
                                      if (lienClaimNumbers[lien.id] !== undefined) {
                                        handleLienClaimSave(lien.id);
                                      }
                                    }}
                                    placeholder="Add claim #"
                                    className={`medication-input ${lienClaimNumbers[lien.id] !== undefined ? 'unsaved' : ''}`}
                                    disabled={savingLien === lien.id}
                                    title="Claim number. Pulled from Filevine when available; type one in if missing."
                                  />
                                </td>
                                <td style={{ textAlign: 'right' }}>{formatCurrency(lien.original_lien_amount || 0)}</td>
                                <td style={{ textAlign: 'right' }}>{formatCurrency(lien.current_lien_amount || 0)}</td>
                                {/* Final lien amount — what the settlement
                                    letter pays this lienholder. Editable,
                                    save-on-blur, persisted in the overlay.
                                    Kept separate from the negotiated figure
                                    because they can differ. */}
                                <td style={{ textAlign: 'right' }}>
                                  <input
                                    type="text"
                                    inputMode="decimal"
                                    value={
                                      lienFinalAmounts[lien.id] !== undefined
                                        ? lienFinalAmounts[lien.id]
                                        : (lien.final_lien_amount ?? '')
                                    }
                                    onChange={(e) => handleLienFinalChange(lien.id, e.target.value)}
                                    onBlur={() => {
                                      if (lienFinalAmounts[lien.id] !== undefined) {
                                        handleLienFinalSave(lien.id);
                                      }
                                    }}
                                    placeholder="—"
                                    className={`medication-input ${lienFinalAmounts[lien.id] !== undefined ? 'unsaved' : ''}`}
                                    style={{ textAlign: 'right' }}
                                    disabled={savingLien === lien.id}
                                    title="Final lien amount. This is what the settlement letter pays — set it once the lien is resolved."
                                  />
                                </td>
                                {/* Inline negotiation cell — Common Fund only (task #7) */}
                                <td>
                                  {lien.active_negotiation ? (
                                    <div className="med-neg-cell">
                                      <div className="med-neg-row med-neg-row-1">
                                        <span className={`med-neg-dot dot-status-${lien.active_negotiation.status}`} />
                                        <span className="med-neg-status">
                                          {lien.active_negotiation.status.replace('_', ' ')}
                                        </span>
                                      </div>
                                      <div className="med-neg-row med-neg-row-2">
                                        <span className="med-neg-amount">
                                          {formatCurrency(lien.active_negotiation.current_amount || 0)}
                                        </span>
                                        <button
                                          type="button"
                                          className="med-neg-view"
                                          onClick={async () => {
                                            try {
                                              const resp = await axios.get(
                                                `${API_URL}/api/negotiations/${lien.active_negotiation.id}`,
                                                { withCredentials: true }
                                              );
                                              setSelectedNegotiation(resp.data);
                                              setActiveTab('negotiations');
                                              setSelectedProject(null);
                                            } catch (err) {
                                              alert('Failed to open negotiation: ' + (err.response?.data?.detail || err.message));
                                            }
                                          }}
                                          title="Open this negotiation"
                                        >
                                          view
                                        </button>
                                        {(lien.prior_negotiations_count || 0) > 0 && (
                                          <span className="med-neg-prior" title={`${lien.prior_negotiations_count} prior negotiation(s)`}>
                                            +{lien.prior_negotiations_count} prior
                                          </span>
                                        )}
                                      </div>
                                    </div>
                                  ) : (lien.prior_negotiations_count || 0) > 0 ? (
                                    <span className="med-neg-prior" title="All prior negotiations withdrawn or rejected">
                                      {lien.prior_negotiations_count} prior
                                    </span>
                                  ) : (
                                    <span style={{ color: 'var(--text-secondary, #6b7280)' }}>—</span>
                                  )}
                                </td>
                                <td style={{ maxWidth: '20ch', overflow: 'hidden', textOverflow: 'ellipsis' }} title={lien.notes}>
                                  {lien.notes || '—'}
                                </td>
                                <td style={{ textAlign: 'center' }}>
                                  <div style={{ display: 'flex', gap: 6, justifyContent: 'center', flexWrap: 'wrap' }}>
                                    <button
                                      type="button"
                                      className="btn-primary"
                                      style={{ fontSize: '0.78rem', padding: '0.35rem 0.7rem' }}
                                      onClick={() => setCommonFundModal({
                                        lienId: lien.id,
                                        lien,
                                        projectId: selectedProject.id,
                                      })}
                                    >
                                      Common Fund
                                    </button>
                                    {/* Made-whole waiver (task #44). Per-lien
                                        because each carrier/adjuster/claim #
                                        differs — a case-level button would
                                        just make you pick a lien first. */}
                                    <button
                                      type="button"
                                      className="btn-secondary"
                                      style={{ fontSize: '0.78rem', padding: '0.35rem 0.7rem', whiteSpace: 'nowrap' }}
                                      onClick={() => setMadeWholeModal({
                                        lienId: lien.id,
                                        lien,
                                        projectId: selectedProject.id,
                                      })}
                                      title="Generate a Made-Whole Doctrine waiver letter to this lienholder"
                                    >
                                      Waiver letter
                                    </button>
                                  </div>
                                </td>
                              </tr>
                            )) : (
                              <tr>
                                {/* 10 columns since Claim # was added (task #43) */}
                                <td colSpan="10" style={{ padding: '2rem', textAlign: 'center', color: 'var(--text-secondary)' }}>
                                  No liens found for this case.
                                </td>
                              </tr>
                            )}
                          </tbody>
                        </table>
                      </div>
                    )}
                  </div>
                )}

                {activeProjectTab === 'letters' && (
                  <div className="medications-section">
                    <h3>Letters</h3>
                    <LettersTab
                      projectId={selectedProject.id}
                      clientName={displayProject.client_name}
                    />
                  </div>
                )}

                {activeProjectTab === 'maylogic' && (
                  <div className="medications-section">
                    <MayLogicScratchpad
                      medications={displayProject.medications || []}
                      project={displayProject}
                      onSaveCaseDefaults={async (updates) => {
                        try {
                          const res = await axios.patch(
                            `${API_URL}/api/projects/${selectedProject?.id}/case-settings`,
                            updates,
                            { withCredentials: true }
                          );
                          setProjectDetails(prev => prev ? {
                            ...prev,
                            case_negotiation_defaults: res.data,
                          } : prev);
                        } catch (e) {
                          console.warn('Failed to save case defaults', e);
                        }
                      }}
                      onStartNegotiation={(medId, askAmount) => {
                        // Re-use the existing negotiation flow: open the email
                        // modal in 'negotiation' mode for this medication with
                        // the Scratchpad ask AND the case-level defaults
                        // (settlement/fees/costs) all pre-filled.
                        const med = displayProject?.medications?.find(m => m.id === medId);
                        if (!med) {
                          alert('Could not find medication record.');
                          return;
                        }
                        const emails = (med.emails || []).map(e =>
                          typeof e === 'string' ? { email: e, notes: null } : e
                        );
                        setEmailModal({ medicationId: medId, emails });
                        setSelectedEmail(emails.length > 0 ? emails[0].email : '');
                        const seed = new Set();
                        if (emails.length > 0) seed.add(emails[0].email);
                        setSelectedEmails(seed);
                        setNewEmailInput('');
                        setEmailMode('negotiation');
                        const caseDefaults = displayProject.case_negotiation_defaults || {};
                        setNegotiationForm(prev => ({
                          ...prev,
                          legal_costs: caseDefaults.legal_costs != null
                            ? String(caseDefaults.legal_costs)
                            : prev.legal_costs,
                          attorney_fees_percentage: caseDefaults.attorney_fees_pct != null
                            ? String(caseDefaults.attorney_fees_pct)
                            : prev.attorney_fees_percentage,
                          settlement_amount: caseDefaults.settlement_amount != null
                            ? String(caseDefaults.settlement_amount)
                            : prev.settlement_amount,
                          settlement_confidential: caseDefaults.settlement_confidential ?? prev.settlement_confidential,
                          requested_amount: askAmount ? parseFloat(askAmount).toFixed(2) : '',
                        }));
                      }}
                    />
                  </div>
                )}

                {activeProjectTab === 'insurance' && (
                  <div className="medications-section">
                    <h3>Insurance — Med Pay {insuranceData && `(${insuranceData.count || 0})`}</h3>
                    {insuranceLoading && <p style={{ padding: '1rem', color: 'var(--text-secondary)' }}>Loading insurance…</p>}
                    {!insuranceLoading && insuranceData?.error && (
                      <p style={{ padding: '1rem', color: '#dc2626' }}>Could not load insurance: {insuranceData.error}</p>
                    )}
                    {!insuranceLoading && insuranceData && !insuranceData.error && (
                      <div className="medications-table">
                        <table>
                          <thead>
                            <tr>
                              <th>Carrier</th>
                              <th>Policyholder</th>
                              <th>Party</th>
                              <th>Policy #</th>
                              <th>Claim #</th>
                              <th>Adjuster</th>
                              <th style={{ textAlign: 'right' }}>Med Pay Limit</th>
                              <th>Reimbursement</th>
                              <th></th>
                            </tr>
                          </thead>
                          <tbody>
                            {insuranceData.items?.length > 0 ? insuranceData.items.map((ins) => (
                              <tr key={ins.id}>
                                <td><strong>{ins.carrier_name}</strong></td>
                                <td>{ins.policyholder || '—'}</td>
                                <td>{ins.party || '—'}</td>
                                <td>{ins.policy_number || '—'}</td>
                                <td>{ins.claim_number || '—'}</td>
                                <td>{ins.adjuster_name || '—'}</td>
                                <td style={{ textAlign: 'right' }}>{ins.med_pay_limit || '—'}</td>
                                {/* Med Pay reimbursement negotiation, mirroring the
                                    Common Fund cell on the Liens tab. */}
                                <td>
                                  {ins.active_negotiation ? (
                                    <div className="med-neg-cell">
                                      <div className="med-neg-row med-neg-row-1">
                                        <span className={`med-neg-dot dot-status-${ins.active_negotiation.status}`} />
                                        <span className="med-neg-status">
                                          {ins.active_negotiation.status.replace('_', ' ')}
                                        </span>
                                      </div>
                                      <div className="med-neg-row med-neg-row-2">
                                        <span className="med-neg-amount">
                                          {formatCurrency(ins.active_negotiation.current_amount || 0)}
                                        </span>
                                        <button
                                          type="button"
                                          className="med-neg-view"
                                          onClick={async () => {
                                            try {
                                              const resp = await axios.get(
                                                `${API_URL}/api/negotiations/${ins.active_negotiation.id}`,
                                                { withCredentials: true }
                                              );
                                              setSelectedNegotiation(resp.data);
                                              setActiveTab('negotiations');
                                              setSelectedProject(null);
                                            } catch (err) {
                                              alert('Failed to open negotiation: ' + (err.response?.data?.detail || err.message));
                                            }
                                          }}
                                          title="Open this negotiation"
                                        >
                                          view
                                        </button>
                                        {(ins.prior_negotiations_count || 0) > 0 && (
                                          <span className="med-neg-prior" title={`${ins.prior_negotiations_count} prior negotiation(s)`}>
                                            +{ins.prior_negotiations_count} prior
                                          </span>
                                        )}
                                      </div>
                                    </div>
                                  ) : (ins.prior_negotiations_count || 0) > 0 ? (
                                    <span className="med-neg-prior" title="All prior negotiations withdrawn or rejected">
                                      {ins.prior_negotiations_count} prior
                                    </span>
                                  ) : (
                                    <span style={{ color: 'var(--text-secondary)' }}>—</span>
                                  )}
                                </td>
                                <td>
                                  <button
                                    type="button"
                                    className="btn-secondary"
                                    onClick={() => setMedPayModal({ ins, projectId: selectedProject.id })}
                                    title="Med Pay reimbursement letters and negotiation"
                                  >
                                    Med Pay
                                  </button>
                                </td>
                              </tr>
                            )) : (
                              <tr>
                                <td colSpan="9" style={{ padding: '2rem', textAlign: 'center', color: 'var(--text-secondary)' }}>
                                  No Med Pay coverage found for this case.
                                </td>
                              </tr>
                            )}
                          </tbody>
                        </table>
                      </div>
                    )}
                  </div>
                )}
              </>
            )}
          </div>
        </div>

        {medPayModal && (
          <MedPayModal
            modal={medPayModal}
            clientName={displayProject.client_name}
            close={() => setMedPayModal(null)}
            onNegotiationOpened={() => loadInsurance(selectedProject.id)}
          />
        )}

        {madeWholeModal && (
          <MadeWholeWaiverModal
            modal={madeWholeModal}
            clientName={displayProject.client_name}
            close={() => setMadeWholeModal(null)}
          />
        )}

        {commonFundModal && (
          <CommonFundModal
            modal={commonFundModal}
            close={() => setCommonFundModal(null)}
            sending={sendingCommonFund}
            setSending={setSendingCommonFund}
            onSent={() => {
              setCommonFundModal(null);
              // Reload the liens tab so the row reflects the sent status
              if (selectedProject?.id) loadLiens(selectedProject.id);
            }}
          />
        )}

        {emailModal && (
          <div
            style={{
              position: 'fixed',
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              backgroundColor: 'rgba(0, 0, 0, 0.5)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              zIndex: 1000
            }}
            onClick={() => {
              setEmailModal(null);
              setSelectedEmail('');
              setEmailSubject('');
              setEmailBody('');
            }}
          >
            <div 
              className="email-modal-content"
              onClick={(e) => e.stopPropagation()}
            >
              <div className="email-modal-header">
                <h3>{emailMode === 'negotiation' ? 'Start Negotiation' : 'Send Email to Provider'}</h3>
                <button
                  onClick={() => {
                    setEmailModal(null);
                    setSelectedEmail('');
                    setEmailSubject('');
                    setEmailBody('');
                    setEmailMode('email');
                    setNegotiationForm({ legal_costs: '', attorney_fees_percentage: '40', requested_amount: '', settlement_amount: '', settlement_confidential: false });
                  }}
                  className="email-modal-close"
                >
                  ×
                </button>
              </div>

              <div className="mode-toggle">
                <button
                  onClick={() => setEmailMode('email')}
                  className={emailMode === 'email' ? 'active' : ''}
                >
                  Regular Email
                </button>
                <button
                  onClick={() => setEmailMode('negotiation')}
                  className={emailMode === 'negotiation' ? 'active' : ''}
                >
                  Start Negotiation
                </button>
              </div>
              
              {/* Multi-recipient email selection (task #4). Always shows:
                  - all known emails as checkboxes (Filevine + team-added)
                  - an "Add another email" input that's always visible
                  Team can check multiple recipients; Start Negotiation
                  sends to all of them. */}
              <div className="form-group">
                <label>
                  Recipient Email{emailModal.emails.length === 1 ? '' : 's'}
                  {emailModal.emails.length > 0 && (
                    <span style={{ fontWeight: 'normal', fontSize: '0.82rem', color: 'var(--text-secondary)', marginLeft: '0.5rem' }}>
                      ({selectedEmails.size} selected)
                    </span>
                  )}
                </label>
                {emailModal.emails.length === 0 ? (
                  <p style={{ fontSize: '0.9rem', color: 'var(--text-secondary)', margin: '0.25rem 0 0.75rem 0' }}>
                    No provider email on file in Filevine — add one below to continue.
                  </p>
                ) : (
                  <ul className="recipient-checkbox-list">
                    {emailModal.emails.map((emailObj, idx) => {
                      const email = typeof emailObj === 'string' ? emailObj : emailObj.email;
                      const notes = typeof emailObj === 'object' ? emailObj.notes : null;
                      const checked = selectedEmails.has(email);
                      return (
                        <li key={idx} className="recipient-checkbox-row">
                          <label>
                            <input
                              type="checkbox"
                              checked={checked}
                              onChange={(e) => {
                                setSelectedEmails(prev => {
                                  const next = new Set(prev);
                                  if (e.target.checked) {
                                    next.add(email);
                                    // Track the most recently checked as primary
                                    setSelectedEmail(email);
                                  } else {
                                    next.delete(email);
                                    if (selectedEmail === email) {
                                      const remaining = Array.from(next);
                                      setSelectedEmail(remaining[0] || '');
                                    }
                                  }
                                  return next;
                                });
                              }}
                            />
                            <span className="recipient-email">{email}</span>
                            {notes && <span className="recipient-note">({notes})</span>}
                          </label>
                        </li>
                      );
                    })}
                  </ul>
                )}
                <div style={{ display: 'flex', gap: '0.5rem', alignItems: 'center', marginTop: '0.5rem' }}>
                  <input
                    type="email"
                    value={newEmailInput}
                    onChange={(e) => setNewEmailInput(e.target.value)}
                    placeholder="Add another email (e.g. liens.dept@provider.com)"
                    style={{ flex: 1 }}
                    disabled={savingNewEmail}
                  />
                  <button
                    onClick={handleSaveNewEmail}
                    disabled={savingNewEmail || !newEmailInput.trim()}
                    className="btn-submit"
                    style={{ whiteSpace: 'nowrap' }}
                    type="button"
                  >
                    {savingNewEmail ? 'Saving…' : 'Add email'}
                  </button>
                </div>
                <p style={{ fontSize: '0.82rem', color: 'var(--text-secondary)', marginTop: '0.4rem' }}>
                  Added emails are saved on the medication overlay so they show up next time and merge with any future Filevine updates.
                </p>
              </div>

              {emailMode === 'email' ? (
                <>
                  <div className="form-group">
                    <label>
                      Subject:
                    </label>
                    <input
                      type="text"
                      value={emailSubject}
                      onChange={(e) => setEmailSubject(e.target.value)}
                      placeholder="Email subject"
                    />
                  </div>
                  
                  <div className="form-group">
                    <label>
                      Message:
                    </label>
                    <textarea
                      value={emailBody}
                      onChange={(e) => setEmailBody(e.target.value)}
                      placeholder="Email body"
                      rows={8}
                    />
                  </div>
                  
                  <div className="form-actions-modal">
                    <button
                      onClick={() => {
                        setEmailModal(null);
                        setSelectedEmail('');
                        setEmailSubject('');
                        setEmailBody('');
                        setEmailMode('email');
                      }}
                      className="btn-cancel"
                    >
                      Cancel
                    </button>
                    <button
                      onClick={handleSendEmail}
                      disabled={sendingEmail || !selectedEmail || !emailSubject.trim() || !emailBody.trim()}
                      className="btn-submit"
                    >
                      {sendingEmail ? 'Sending...' : 'Send Email'}
                    </button>
                  </div>
                </>
              ) : (
                <>
                  {/* First-time-fill banner: shown when the project has no
                      case_negotiation_defaults yet. Encourages the attorney
                      to enter these values once so subsequent negotiations
                      on the same case auto-populate. */}
                  {(() => {
                    const cd = displayProject?.case_negotiation_defaults || {};
                    const isFirstTime =
                      cd.settlement_amount == null &&
                      cd.legal_costs == null;
                    if (!isFirstTime) return null;
                    return (
                      <div style={{
                        background: 'linear-gradient(135deg, rgba(161,184,8,0.12) 0%, rgba(161,184,8,0.04) 100%)',
                        border: '1px solid #A1B808',
                        borderRadius: 6,
                        padding: '12px 14px',
                        marginBottom: '1rem',
                        display: 'flex',
                        alignItems: 'flex-start',
                        gap: 10,
                      }}>
                        <div style={{
                          width: 24, height: 24, background: '#A1B808', borderRadius: '50%',
                          color: 'white', display: 'flex', alignItems: 'center',
                          justifyContent: 'center', fontSize: 14, flexShrink: 0,
                        }}>💡</div>
                        <div>
                          <div style={{ fontWeight: 700, color: '#171D29', fontSize: 13, marginBottom: 4 }}>
                            First negotiation on this case
                          </div>
                          <div style={{ fontSize: 12, color: '#4F5A04', lineHeight: 1.5 }}>
                            Enter settlement, attorney fees, and costs below —
                            <strong> they'll auto-fill for every future negotiation on this case</strong>.
                            You can also enter/edit them at the top of the project page.
                          </div>
                        </div>
                      </div>
                    );
                  })()}

                  <div className="form-group">
                    <label>
                      Out-of-Pocket Legal Costs ($):
                    </label>
                    <input
                      type="number"
                      step="0.01"
                      min="0"
                      value={negotiationForm.legal_costs}
                      onChange={(e) => setNegotiationForm(prev => ({ ...prev, legal_costs: e.target.value }))}
                      placeholder="0.00"
                    />
                  </div>
                  
                  <div className="form-group">
                    <label>
                      Attorney Fees Percentage (%):
                    </label>
                    <input
                      type="number"
                      step="0.1"
                      min="0"
                      max="100"
                      value={negotiationForm.attorney_fees_percentage}
                      onChange={(e) => setNegotiationForm(prev => ({ ...prev, attorney_fees_percentage: e.target.value }))}
                      placeholder="40"
                    />
                  </div>
                  
                  {/* MayLogic card — historical reduction stats for this
                      provider. Renders ABOVE the requested-amount input so the
                      attorney sees a data-backed suggestion before typing.
                      Degrades to nothing if the warehouse is unreachable. */}
                  {(() => {
                    const targetMed = displayProject?.medications?.find(m => m.id === emailModal?.medicationId);
                    if (!targetMed) return null;
                    // The "billed" basis MayLogic uses is the originally-billed
                    // amount from the provider, not the lien claim. Fall back to
                    // lien_amount only if original_bill isn't set.
                    const billed = parseFloat(
                      targetMed.original_bill ?? targetMed.lien_amount ?? targetMed.amount ?? 0
                    ) || null;
                    return (
                      <MayLogicCard
                        providerName={targetMed.provider_name}
                        billedAmount={billed}
                        currentAmount={negotiationForm.requested_amount}
                        onApplyAmount={(amount) =>
                          setNegotiationForm(prev => ({ ...prev, requested_amount: amount }))
                        }
                      />
                    );
                  })()}

                  <div className="form-group">
                    <label>
                      Requested Reduction Amount ($):
                    </label>
                    {/* Task #42 — cursor-jump fix.
                        Was type="number". When MayLogic pre-fills a value
                        like "1500.00" and you backspace in the MIDDLE of
                        it, the browser's number-input normalization reports
                        a transiently-invalid value, React re-renders, and
                        the caret snaps to the end — so edits landed in the
                        wrong place. type="text" + inputMode="decimal" keeps
                        the raw string exactly as typed (mobile still gets a
                        numeric keypad), and we sanitize to digits + a single
                        decimal point. parseFloat on submit is unchanged. */}
                    <input
                      type="text"
                      inputMode="decimal"
                      value={negotiationForm.requested_amount}
                      onChange={(e) => {
                        const clean = sanitizeDecimalInput(e.target.value);
                        if (clean !== null) {
                          setNegotiationForm(prev => ({ ...prev, requested_amount: clean }));
                        }
                      }}
                      placeholder="0.00"
                    />
                  </div>

                  <div className="form-group">
                    <label>
                      Settlement Amount ($) — optional:
                    </label>
                    <input
                      type="text"
                      inputMode="decimal"
                      value={negotiationForm.settlement_amount}
                      onChange={(e) => {
                        const clean = sanitizeDecimalInput(e.target.value);
                        if (clean !== null) {
                          setNegotiationForm(prev => ({ ...prev, settlement_amount: clean }));
                        }
                      }}
                      placeholder="Leave blank if confidential or not yet known"
                      disabled={negotiationForm.settlement_confidential}
                    />
                  </div>

                  <div className="form-group">
                    <label style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', cursor: 'pointer' }}>
                      <input
                        type="checkbox"
                        checked={negotiationForm.settlement_confidential}
                        onChange={(e) => setNegotiationForm(prev => ({
                          ...prev,
                          settlement_confidential: e.target.checked,
                          // When toggling to confidential, clear the amount so the user doesn't
                          // accidentally send a confidential dollar value in the letter.
                          settlement_amount: e.target.checked ? '' : prev.settlement_amount,
                        }))}
                      />
                      <span>Settlement amount is confidential (use generic phrasing in letter)</span>
                    </label>
                    <p style={{ fontSize: '0.8rem', color: 'var(--text-secondary)', margin: '0.25rem 0 0 1.5rem' }}>
                      Letter will say: <em>{
                        negotiationForm.settlement_confidential
                          ? '"...has settled for a confidential settlement amount."'
                          : (negotiationForm.settlement_amount
                              ? `"...has settled for $${parseFloat(negotiationForm.settlement_amount || 0).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}."`
                              : '"...has settled."')
                      }</em>
                    </p>
                  </div>

                  <div className="form-actions-modal">
                    <button
                      onClick={() => {
                        setEmailModal(null);
                        setSelectedEmail('');
                        setEmailMode('email');
                        setNegotiationForm({ legal_costs: '', attorney_fees_percentage: '40', requested_amount: '', settlement_amount: '', settlement_confidential: false });
                      }}
                      className="btn-cancel"
                    >
                      Cancel
                    </button>
                    <button
                      onClick={handleStartNegotiation}
                      disabled={startingNegotiation || !selectedEmail || !negotiationForm.legal_costs || !negotiationForm.attorney_fees_percentage || !negotiationForm.requested_amount}
                      className="btn-submit"
                    >
                      {startingNegotiation ? 'Sending...' : 'Start Negotiation'}
                    </button>
                  </div>
                </>
              )}
            </div>
          </div>
        )}
      </div>
    );
  };

  // The moment before we know who is signed in. Same ground as the sign-in
  // screen it usually becomes, so the page does not flash a different
  // colour on the way there.
  const loadingShell = (
    <div className="lm-ff lm-login" data-workspace-theme="dark">
      <div className="lm-login-inner">
        <img className="lm-login-logo" src="/brand/lm-logo.png" alt="Lien Machine"
             width="482" height="186" />
        <p className="lm-subtitle">Signing you in…</p>
      </div>
    </div>
  );

  if (authLoading && isAuthenticated) {
    return loadingShell;
  }

  if (!isAuthenticated) {
    return authLoading ? loadingShell : <LoginPage />;
  }

  if (authLoading) {
    return loadingShell;
  }

  return (
    // No top bar. Once the department name came out of it the only thing
    // left was the user chip, so it was a full-width rule carrying one
    // control — and its hamburger toggled a sidebarOpen nothing ever read.
    // Sign-out moved into ProfileMenu, which every tab renders.
    // One attribute carries the whole light/dark split: the dashboard is the
    // cinematic dark workspace, every other tab is warm ivory. The sidebar
    // stays dark either way, which is why the attribute sits on the shell
    // and the light rules are scoped to .lm-content inside it.
    <div className="lm-ff" data-workspace-theme={activeTab === 'dashboard' ? 'dark' : 'light'}>
      <div className="lm-shell">
        {/* projectCount falls back to the dashboard's in-scope count: the
            project list only loads on the Cases tab, so the badge was blank
            until you had been there. Same population, available sooner. */}
        <Sidebar
          activeTab={activeTab}
          setActiveTab={setActiveTab}
          projectCount={pagination?.total || projects.length || (stats && stats.total_projects) || 0}
          negotiationsNeedReview={(stats && stats.negotiations_in_negotiation) || 0}
          closeoutCount={(stats && stats.open_closeouts) || 0}
        />

        <main className="lm-content">
          {/* The dashboard puts the profile in its own header row, beside
              the date. Every other tab still needs it, and gets it here
              until each one grows a page header of its own. */}
          {activeTab !== 'dashboard' && (
            <div className="lm-top">
              <span>Workspace / {TAB_TITLES[activeTab] || activeTab}</span>
              <ProfileMenu user={user} onLogout={handleLogout} />
            </div>
          )}
          {activeTab === 'dashboard' && (
            <Dashboard
              stats={stats}
              loading={loading}
              user={user}
              onRefresh={handleRefreshProjectsList}
              refreshing={refreshingList}
              onNewNegotiation={() => setActiveTab('projects')}
              setActiveTab={setActiveTab}
              onLogout={handleLogout}
            />
          )}
          {activeTab === 'projects' && (
            <ProjectsList
              projects={projects}
              pagination={pagination}
              pageSize={pageSize}
              searchTerm={searchTerm}
              setSearchTerm={setSearchTerm}
              phaseFilter={phaseFilter}
              setPhaseFilter={setPhaseFilter}
              allowedPhases={[PHASE_FILTER_ALL, ...(ALLOWED_PHASES.length > 0 ? ALLOWED_PHASES : apiPhases)]}
              currentPage={currentPage}
              setCurrentPage={setCurrentPage}
              setSelectedProject={setSelectedProject}
              loading={loading}
              refreshing={refreshingList}
              onRefresh={handleRefreshProjectsList}
            />
          )}
          {activeTab === 'negotiations' && <NegotiationsList />}
          {activeTab === 'reconciliation' && <ProviderReconciliation />}
          {activeTab === 'providers' && <ProviderLookup />}
          {activeTab === 'closeout' && <CloseoutApprovals />}
        </main>
      </div>

      {selectedProject && <ProjectModal />}
    </div>
  );
}

export default App;
